// src/components/games/crossroads/cardData.ts
// Card definitions for Crossroads: The Divergent Realms TCG

import {
  Card,
  CommanderCard,
  CreatureCard,
  ArtCard,
  ManaSourceCard,
  ElementType,
  CardType,
  Rarity,
  AbilityKeyword,
  DeckDefinition,
  GameState,
  TargetSelection,
  OngoingEffectType,
  OngoingEffectDuration,
  BattlefieldCreature,
  CreatureAbility,
} from './types';
import {
  drawCards,
  dealDamage,
  healCommander,
  healCreature,
  modifyCreaturePower,
  addMana,
  addPreventionShield,
  rollDie,
  rollDieTracked,
  rerollLastDie,
  createToken,
  destroyCreature,
  returnCreatureToHand,
  discardRandomCard,
  sacrificeCreature,
  getOpponentId,
  returnCardFromGraveyard,
  flipCoin,
  canAffordCost,
  payCost,
  getAllCreatures,
  copyCreatureStats,
  transformCreatureIntoCopy,
  tapCreature,
  findCreatureOnBattlefield,
  requestTargets,
  didCreatureDamageAnyGM,
  didCreatureDamageGM,
  generateInstanceId,
  logEvent,
  createChooseOneModal,
  createBinaryChoice,
  createRevealInfoModal,
  getDamageReceivedByCreature,
  addOngoingEffect,
  swapCreatureControl,
  addCardCostReduction,
  applyTemporaryTransformation,
} from './gameLogic';

// ============================================================================
// FIRE DECK - Dice and Autism
// ============================================================================

// COMMANDER
export const DICE_AND_AUTISM: CommanderCard = {
  id: 'cmd_fire_dice_autism',
  name: 'Dice and Autism',
  type: CardType.COMMANDER,
  pronouns: 'He/They',
  subtitle: 'Game Master - Changeling Fighter/Bard',
  health: 30,
  elementType: ElementType.FIRE,
  flavorText: 'To tell Stories means you have to experience joyous chaos and mellow sadness as well.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/CardFrontFrankRed.png',
  abilities: [
    {
      id: 'dice_autism_mana',
      cost: {},
      tapCost: true,
      description: 'TAP: Gain 1 Fire Mana',
      effect: (game: GameState, playerId: number) => {
        addMana(game.players[playerId].manaPool, { [ElementType.FIRE]: 1 });
      },
    },
    {
      id: 'dice_autism_damage',
      cost: { [ElementType.FIRE]: 1 },
      description: 'Roll a d6. Deal that much damage to any target',
      effect: (game: GameState, playerId: number) => {
        // Deal to opponent commander - use tracked roll for reroll ability
        const opponentId = getOpponentId(playerId);
        const targetId = `player_${opponentId}`;
        const damage = rollDieTracked(game, 6, playerId, 'damage', targetId, 'player');
        dealDamage(game, targetId, 'player', damage);
      },
    },
    {
      id: 'dice_autism_buff',
      cost: { [ElementType.FIRE]: 2 },
      description: 'Target creature you control gets +3 Power until end of turn. If it defeats a creature this turn, draw a card.',
      effect: (game: GameState, playerId: number) => {
        // Buff first creature
        const player = game.players[playerId];
        if (player.battlefield.creatures.length > 0) {
          modifyCreaturePower(game, player.battlefield.creatures[0].instanceId, 3, 0);
        }
      },
    },
    {
      id: 'dice_autism_reroll',
      cost: { [ElementType.FIRE]: 1 },
      description: 'Reroll any die roll made this turn. Use this ability only once per turn.',
      effect: (game: GameState, playerId: number) => {
        const newValue = rerollLastDie(game);
        if (newValue === null) {
          logEvent(game, 'ability_activated', 'No die roll available to reroll');
        } else {
          logEvent(game, 'ability_activated', `Die rerolled! New value: ${newValue}`);
        }
      },
      usesPerTurn: 1,
    },
  ],
};

// CREATURES
export const EMBER_IMP: CreatureCard = {
  id: 'cre_fire_ember_imp',
  name: 'Ember Imp',
  type: CardType.CREATURE,
  cost: { [ElementType.FIRE]: 1 },
  power: 2,
  health: 1,
  rarity: Rarity.COMMON,
  subtype: 'Elemental, Imp',
  keywords: [],
  flavorText: 'Quick and dangerous, just how we like it.',
};

export const FLAME_DANCER: CreatureCard = {
  id: 'cre_fire_flame_dancer',
  name: 'Flame Dancer',
  type: CardType.CREATURE,
  cost: { [ElementType.FIRE]: 2 },
  power: 3,
  health: 2,
  rarity: Rarity.COMMON,
  subtype: 'Elemental',
  keywords: [],
  flavorText: 'The fire doesn\'t burn them—it obeys them.',
  abilities: [
    {
      id: 'flame_dancer_etb',
      description: 'When this card is played, deal 1 damage to any viable target.',
      trigger: 'enter',
      effect: (game: GameState, creatureId: string) => {
        const creature = findCreatureOnBattlefield(game, creatureId);
        if (!creature) return;

        requestTargets(game, {
          playerId: creature.ownerId,
          title: 'Flame Dancer',
          description: 'Deal 1 damage to any target',
          targetType: 'any',
          sourceCardId: 'cre_fire_flame_dancer',
          sourceAbilityId: 'flame_dancer_etb',
        }, (selection: TargetSelection) => {
          if (selection.creatures && selection.creatures.length > 0) {
            dealDamage(game, selection.creatures[0], 'creature', 1, creatureId);
          } else if (selection.players && selection.players.length > 0) {
            dealDamage(game, `player_${selection.players[0]}`, 'player', 1, creatureId);
          }
        });
      },
    },
  ],
};

export const RECKLESS_BERSERKER: CreatureCard = {
  id: 'cre_fire_reckless_berserker',
  name: 'Reckless Berserker',
  type: CardType.CREATURE,
  cost: { [ElementType.FIRE]: 2 },
  power: 4,
  health: 1,
  rarity: Rarity.UNCOMMON,
  subtype: 'Human, Warrior',
  keywords: [],
  flavorText: 'Pain is just another story to tell.',
  abilities: [
    {
      id: 'berserker_attack',
      description: 'When this creature attacks, flip a coin. If tails, it deals 1 damage to your GM.',
      trigger: 'attack',
      effect: (game: GameState, creatureId: string) => {
        const creature = game.players.flatMap((p) => p.battlefield.creatures).find((c) => c.instanceId === creatureId);
        if (creature) {
          const result = flipCoin();
          if (!result) { // tails
            dealDamage(game, `player_${creature.ownerId}`, 'player', 1);
          }
        }
      },
    },
  ],
};

export const MAGMA_HOUND: CreatureCard = {
  id: 'cre_fire_magma_hound',
  name: 'Magma Hound',
  type: CardType.CREATURE,
  cost: { [ElementType.FIRE]: 3 },
  power: 4,
  health: 3,
  rarity: Rarity.UNCOMMON,
  subtype: 'Elemental, Beast',
  keywords: [],
  flavorText: 'It hunts by the heat of your fear.',
  abilities: [
    {
      id: 'magma_bonus_damage',
      description: 'When this creature attacks, flip coin. If heads, deals 1 additional damage to its original target.',
      trigger: 'attack',
      effect: (game: GameState, creatureId: string) => {
        const result = flipCoin();
        if (result) {
          // heads - deal bonus damage
          logEvent(game, 'ability_activated', 'Magma Hound\'s attack burns hotter! (+1 damage)');

          // Get the attack target from tracking
          const attackTarget = game.attackTargets?.[creatureId];
          if (attackTarget) {
            dealDamage(game, attackTarget.targetId, attackTarget.targetType, 1, creatureId);
          }
        } else {
          logEvent(game, 'ability_activated', 'Magma Hound\'s attack fizzles.');
        }
      },
    },
  ],
};

export const WILDFIRE_PHOENIX: CreatureCard = {
  id: 'cre_fire_wildfire_phoenix',
  name: 'Wildfire Phoenix',
  type: CardType.CREATURE,
  cost: { [ElementType.FIRE]: 4 },
  power: 5,
  health: 3,
  rarity: Rarity.RARE,
  subtype: 'Elemental, Phoenix',
  keywords: [AbilityKeyword.FLYING],
  flavorText: 'Ashes are just the beginning.',
  abilities: [
    {
      id: 'phoenix_rebirth',
      cost: { [ElementType.FIRE]: 2 },
      description: 'When this creature is defeated, you may pay 2 Fire. If you do, return it to your hand.',
      trigger: 'defeat',
      effect: (game: GameState, creatureId: string) => {
        // Find which player owned this creature
        const creature = game.players.flatMap((p) => p.battlefield.creatures).find((c) => c.instanceId === creatureId);
        if (!creature) {
          // Creature already removed, check discard piles for the card
          for (const player of game.players) {
            const cardIndex = player.discard.findIndex(id => id === 'cre_fire_wildfire_phoenix');
            if (cardIndex !== -1 && canAffordCost(player.manaPool, { [ElementType.FIRE]: 2 })) {
              // Ask player if they want to pay
              createBinaryChoice(
                game,
                player.id,
                'Wildfire Phoenix',
                'Pay 2 Fire to return Phoenix to hand?',
                () => {
                  // Yes - pay and return
                  payCost(player.manaPool, { [ElementType.FIRE]: 2 });
                  player.discard.splice(cardIndex, 1);
                  player.hand.push('cre_fire_wildfire_phoenix');
                },
                () => {
                  // No - phoenix stays in discard
                },
                'Pay & Return',
                'Stay Defeated',
                'cre_fire_wildfire_phoenix'
              );
              break;
            }
          }
        }
      },
    },
  ],
};

// ARTS
export const SPARK: ArtCard = {
  id: 'art_fire_spark',
  name: 'Spark',
  type: CardType.ART,
  cost: { [ElementType.FIRE]: 1 },
  rarity: Rarity.COMMON,
  flavorText: 'Every inferno starts with a single flame.',
  targetType: 'any',
  targetCount: 1,
  effect: (game: GameState, playerId: number, targets?: TargetSelection) => {
    // Use targets provided by UI layer
    if (targets) {
      if (targets.creatures && targets.creatures.length > 0) {
        dealDamage(game, targets.creatures[0], 'creature', 2);
      } else if (targets.players && targets.players.length > 0) {
        dealDamage(game, `player_${targets.players[0]}`, 'player', 2);
      }
    }
  },
};

export const BURNING_AMBITION: ArtCard = {
  id: 'art_fire_burning_ambition',
  name: 'Burning Ambition',
  type: CardType.ART,
  cost: { [ElementType.FIRE]: 2 },
  rarity: Rarity.COMMON,
  flavorText: 'Sometimes you just need to go all in.',
  targetType: 'none',
  effect: (game: GameState, playerId: number) => {
    const player = game.players[playerId];
    // Buff first creature +3/+0 and deal 1 to own commander
    if (player.battlefield.creatures.length > 0) {
      modifyCreaturePower(game, player.battlefield.creatures[0].instanceId, 3, 0);
      dealDamage(game, playerId.toString(), 'player', 1);
    }
  },
};

export const CHAOS_BOLT: ArtCard = {
  id: 'art_fire_chaos_bolt',
  name: 'Chaos Bolt',
  type: CardType.ART,
  cost: { [ElementType.FIRE]: 2 },
  rarity: Rarity.UNCOMMON,
  flavorText: 'Power demands sacrifice.',
  targetType: 'any',
  targetCount: 1,
  effect: (game: GameState, playerId: number) => {
    // Roll first (without tracking target yet)
    const damage = rollDieTracked(game, 6, playerId, 'none');
    requestTargets(
      game,
      {
        playerId,
        title: 'Chaos Bolt',
        description: `Deal ${damage} damage to any target`,
        targetType: 'any',
      },
      (selection: TargetSelection) => {
        let targetId: string | undefined;
        let targetType: 'player' | 'creature' | undefined;

        if (selection.creatures && selection.creatures.length > 0) {
          targetId = selection.creatures[0];
          targetType = 'creature';
          dealDamage(game, targetId, targetType, damage);
        } else if (selection.players && selection.players.length > 0) {
          targetId = `player_${selection.players[0]}`;
          targetType = 'player';
          dealDamage(game, targetId, targetType, damage);
        }

        // Update tracking with target information for reroll support
        if (game.lastDiceRoll && targetId && targetType) {
          game.lastDiceRoll.effectType = 'damage';
          game.lastDiceRoll.targetId = targetId;
          game.lastDiceRoll.targetType = targetType;
        }
      }
    );
  },
};

export const PYROCLASM: ArtCard = {
  id: 'art_fire_pyroclasm',
  name: 'Pyroclasm',
  type: CardType.ART,
  cost: { [ElementType.FIRE]: 3 },
  rarity: Rarity.UNCOMMON,
  flavorText: 'When subtlety fails, there\'s always fire.',
  targetType: 'none',
  effect: (game: GameState, playerId: number) => {
    // Deal 2 damage to all creatures
    for (const player of game.players) {
      for (const creature of player.battlefield.creatures) {
        dealDamage(game, creature.instanceId, 'creature', 2);
      }
    }
  },
};

export const VOLCANIC_ERUPTION: ArtCard = {
  id: 'art_fire_volcanic_eruption',
  name: 'Volcanic Eruption',
  type: CardType.ART,
  cost: { [ElementType.FIRE]: 4 },
  rarity: Rarity.RARE,
  flavorText: 'The mountain\'s fury cannot be contained.',
  targetType: 'none',
  effect: (game: GameState, playerId: number) => {
    // Deal 6 damage to opponent commander
    const opponentId = playerId === 0 ? 1 : 0;
    dealDamage(game, `player_${opponentId}`, 'player', 6);
  },
};

// MANA SOURCES
export const VOLCANIC_CRAG: ManaSourceCard = {
  id: 'mana_fire_volcanic_crag',
  name: 'Volcanic Crag',
  type: CardType.MANA_SOURCE,
  rarity: Rarity.COMMON,
  elementType: ElementType.FIRE,
  manaGenerated: { [ElementType.FIRE]: 1 },
  flavorText: 'Limitless potential, both creative and destructive.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/VolcanicCrag.png',
};

export const RAGING_CALDERA: ManaSourceCard = {
  id: 'mana_fire_raging_caldera',
  name: 'Raging Caldera',
  type: CardType.MANA_SOURCE,
  cost: { [ElementType.FIRE]: 1 },
  rarity: Rarity.UNCOMMON,
  elementType: ElementType.FIRE,
  manaGenerated: { [ElementType.FIRE]: 2 },
  flavorText: 'Power demands sacrifice.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/RagingCaldera.png',
  abilities: [
    {
      id: 'caldera_damage',
      description: 'Deal 1 damage to your GM when you tap this.',
      effect: (game: GameState, playerId: number) => {
        dealDamage(game, `player_${playerId}`, 'player', 1);
      },
    },
  ],
};

// Fire Deck Definition
export const FIRE_DECK: DeckDefinition = {
  name: 'Fire Starter Deck',
  elementType: ElementType.FIRE,
  commanderId: 'cmd_fire_dice_autism',
  cards: [
    { cardId: 'cre_fire_ember_imp', count: 3 },
    { cardId: 'cre_fire_flame_dancer', count: 3 },
    { cardId: 'cre_fire_reckless_berserker', count: 2 },
    { cardId: 'cre_fire_magma_hound', count: 2 },
    { cardId: 'cre_fire_wildfire_phoenix', count: 1 },
    { cardId: 'art_fire_spark', count: 3 },
    { cardId: 'art_fire_burning_ambition', count: 3 },
    { cardId: 'art_fire_chaos_bolt', count: 2 },
    { cardId: 'art_fire_pyroclasm', count: 2 },
    { cardId: 'art_fire_volcanic_eruption', count: 1 },
    { cardId: 'mana_fire_volcanic_crag', count: 2 },
    { cardId: 'mana_fire_raging_caldera', count: 1 },
  ],
};

// ============================================================================
// WATER DECK - Nic
// ============================================================================

// COMMANDER
export const NIC: CommanderCard = {
  id: 'cmd_water_nic',
  name: 'Nic',
  type: CardType.COMMANDER,
  pronouns: 'He/They',
  subtitle: 'Game Master - Aasimar Cleric',
  health: 30,
  elementType: ElementType.WATER,
  flavorText: 'Life is an adventure, Roll for Initiative!',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/CardFrontNic.png',
  abilities: [
    {
      id: 'nic_mana',
      cost: {},
      tapCost: true,
      description: 'TAP: Gain 1 Water Mana',
      effect: (game: GameState, playerId: number) => {
        addMana(game.players[playerId].manaPool, { [ElementType.WATER]: 1 });
      },
    },
    {
      id: 'nic_heal',
      cost: { [ElementType.WATER]: 1 },
      description: 'Heal 2 damage from your GM or target creature',
      effect: (game: GameState, playerId: number) => {
        healCommander(game, playerId, 2);
      },
    },
    {
      id: 'nic_buff',
      cost: { [ElementType.WATER]: 2 },
      description: 'Gain 2 Water Mana. Target creature you control gets +1/+2 until end of turn.',
      effect: (game: GameState, playerId: number) => {
        const player = game.players[playerId];
        addMana(player.manaPool, { [ElementType.WATER]: 2 });
        if (player.battlefield.creatures.length > 0) {
          modifyCreaturePower(game, player.battlefield.creatures[0].instanceId, 1, 2);
        }
      },
    },
    {
      id: 'nic_draw',
      cost: { [ElementType.WATER]: 3 },
      description: 'Draw two cards, then return a card from your hand to the top of your deck',
      effect: (game: GameState, playerId: number) => {
        const player = game.players[playerId];
        drawCards(game, playerId, 2);
        // Put last card from hand on top of deck
        if (player.hand.length > 0) {
          const cardToReturn = player.hand.pop()!;
          player.deck.unshift(cardToReturn);
        }
      },
    },
  ],
};

// CREATURES
export const TIDE_SPRITE: CreatureCard = {
  id: 'cre_water_tide_sprite',
  name: 'Tide Sprite',
  type: CardType.CREATURE,
  cost: { [ElementType.WATER]: 1 },
  power: 1,
  health: 2,
  rarity: Rarity.COMMON,
  subtype: 'Elemental, Sprite',
  keywords: [],
  flavorText: 'Small blessings from the current.',
  abilities: [
    {
      id: 'tide_sprite_heal',
      description: 'When this creature enters play, you may heal 2 damage from your GM.',
      trigger: 'enter',
      effect: (game: GameState, creatureId: string) => {
        const creature = game.players.flatMap((p) => p.battlefield.creatures).find((c) => c.instanceId === creatureId);
        if (creature) {
          healCommander(game, creature.ownerId, 2);
        }
      },
    },
  ],
};

export const SILVERSCALE_SCOUT: CreatureCard = {
  id: 'cre_water_silverscale_scout',
  name: 'Silverscale Scout',
  type: CardType.CREATURE,
  cost: { [ElementType.WATER]: 2 },
  power: 2,
  health: 2,
  rarity: Rarity.COMMON,
  subtype: 'Merfolk',
  keywords: [],
  flavorText: 'Alone they are small. Together, unstoppable.',
  abilities: [
    {
      id: 'merfolk_synergy',
      description: 'If you control three or more Merfolk, all Merfolk you control get +1/+1.',
      trigger: 'turn_start',
      effect: (game: GameState, creatureId: string) => {
        const creature = game.players.flatMap((p) => p.battlefield.creatures).find((c) => c.instanceId === creatureId);
        if (!creature) return;

        const player = game.players[creature.ownerId];
        const fishCreatures = player.battlefield.creatures.filter((c) => {
          const card = game.cardInstances[c.cardId] as CreatureCard;
          return card && card.subtype === 'Fish';
        });

        if (fishCreatures.length >= 3) {
          for (const fish of fishCreatures) {
            modifyCreaturePower(game, fish.instanceId, 1, 1);
          }
        }
      },
    },
  ],
};

export const RIVER_HEALER: CreatureCard = {
  id: 'cre_water_river_healer',
  name: 'River Healer',
  type: CardType.CREATURE,
  cost: { [ElementType.WATER]: 2 },
  power: 1,
  health: 3,
  rarity: Rarity.UNCOMMON,
  subtype: 'Elemental',
  keywords: [],
  flavorText: 'The waters restore what was lost.',
  abilities: [
    {
      id: 'healer_activate',
      description: 'TAP: Heal 2 to target creature or GM.',
      trigger: 'activated',
      tapCost: true,
      effect: (game: GameState, creatureId: string) => {
        const creature = findCreatureOnBattlefield(game, creatureId);
        if (!creature) return;

        requestTargets(game, {
          playerId: creature.ownerId,
          title: 'River Healer',
          description: 'Heal 2 damage to target creature or GM',
          targetType: 'any',
          sourceCardId: 'cre_water_river_healer',
          sourceAbilityId: 'healer_activate',
        }, (selection: TargetSelection) => {
          if (selection.creatures && selection.creatures.length > 0) {
            healCreature(game, selection.creatures[0], 2);
          } else if (selection.players && selection.players.length > 0) {
            healCommander(game, selection.players[0], 2);
          }
        });
      },
    },
  ],
};

export const CORAL_GUARDIAN: CreatureCard = {
  id: 'cre_water_coral_guardian',
  name: 'Coral Guardian',
  type: CardType.CREATURE,
  cost: { [ElementType.WATER]: 2 },
  power: 1,
  health: 3,
  rarity: Rarity.UNCOMMON,
  subtype: 'Merfolk',
  keywords: [],
  flavorText: 'The guardian watches over the school.',
  abilities: [
    {
      id: 'guardian_buff',
      description: 'TAP: Target creature gets +0/+1. If that creature is a merfolk, it instead gets +1/+1.',
      trigger: 'activated',
      tapCost: true,
      effect: (game: GameState, creatureId: string) => {
        const creature = findCreatureOnBattlefield(game, creatureId);
        if (!creature) return;

        requestTargets(game, {
          playerId: creature.ownerId,
          title: 'Coral Guardian',
          description: 'Target creature gets +0/+1 (+1/+1 if Merfolk)',
          targetType: 'creature',
          sourceCardId: 'cre_water_coral_guardian',
          sourceAbilityId: 'guardian_buff',
        }, (selection: TargetSelection) => {
          if (selection.creatures && selection.creatures.length > 0) {
            const targetCreature = findCreatureOnBattlefield(game, selection.creatures[0]);
            if (targetCreature) {
              const targetCard = ALL_CARDS[targetCreature.cardId] as CreatureCard;
              const isMerfolk = targetCard?.subtype?.includes('Merfolk');

              if (isMerfolk) {
                modifyCreaturePower(game, targetCreature.instanceId, 1, 1);
              } else {
                modifyCreaturePower(game, targetCreature.instanceId, 0, 1);
              }
            }
          }
        });
      },
    },
  ],
};

export const MOONLIT_SERPENT: CreatureCard = {
  id: 'cre_water_moonlit_serpent',
  name: 'Moonlit Serpent',
  type: CardType.CREATURE,
  cost: { [ElementType.WATER]: 3 },
  power: 2,
  health: 4,
  rarity: Rarity.UNCOMMON,
  subtype: 'Sea Serpent',
  keywords: [],
  flavorText: 'Wisdom flows like the midnight tide.',
  abilities: [
    {
      id: 'serpent_draw',
      description: 'When this creature enters play, draw a card.',
      trigger: 'enter',
      effect: (game: GameState, creatureId: string) => {
        const creature = game.players.flatMap((p) => p.battlefield.creatures).find((c) => c.instanceId === creatureId);
        if (creature) {
          drawCards(game, creature.ownerId, 1);
        }
      },
    },
  ],
};

export const LEVIATHAN_OF_THE_DEEP: CreatureCard = {
  id: 'cre_water_leviathan',
  name: 'Leviathan of the Deep',
  type: CardType.CREATURE,
  cost: { [ElementType.WATER]: 5 },
  power: 5,
  health: 5,
  rarity: Rarity.RARE,
  subtype: 'Monster',
  keywords: [],
  flavorText: 'From the abyss it rises, and all who witness are changed.',
  abilities: [
    {
      id: 'leviathan_enter',
      description: 'When this creature enters play, all other creatures you control get +1/+1 until end of turn and you may draw a card for each Merfolk creature you control.',
      trigger: 'enter',
      effect: (game: GameState, creatureId: string) => {
        const creature = game.players.flatMap((p) => p.battlefield.creatures).find((c) => c.instanceId === creatureId);
        if (!creature) return;

        const player = game.players[creature.ownerId];

        // Buff all other creatures
        for (const c of player.battlefield.creatures) {
          if (c.instanceId !== creatureId) {
            modifyCreaturePower(game, c.instanceId, 1, 1);
          }
        }

        // Draw for each Fish
        const fishCount = player.battlefield.creatures.filter((c) => {
          const card = game.cardInstances[c.cardId] as CreatureCard;
          return card && card.subtype === 'Fish';
        }).length;

        if (fishCount > 0) {
          drawCards(game, creature.ownerId, fishCount);
        }
      },
    },
  ],
};

// ARTS
export const REFRESHING_RAIN: ArtCard = {
  id: 'art_water_refreshing_rain',
  name: 'Refreshing Rain',
  type: CardType.ART,
  cost: { [ElementType.WATER]: 1 },
  rarity: Rarity.COMMON,
  flavorText: 'Every drop brings renewal.',
  targetType: 'none',
  effect: (game: GameState, playerId: number) => {
    // Heal 3 to own commander
    healCommander(game, playerId, 3);
  },
};

export const FLOWING_GRACE: ArtCard = {
  id: 'art_water_flowing_grace',
  name: 'Flowing Grace',
  type: CardType.ART,
  cost: { [ElementType.WATER]: 2 },
  rarity: Rarity.COMMON,
  flavorText: 'Adapt and endure.',
  targetType: 'none',
  effect: (game: GameState, playerId: number) => {
    const player = game.players[playerId];
    // Buff first creature +1/+1 and draw a card
    if (player.battlefield.creatures.length > 0) {
      modifyCreaturePower(game, player.battlefield.creatures[0].instanceId, 1, 1);
    }
    drawCards(game, playerId, 1);
  },
};

export const TIDAL_WISDOM: ArtCard = {
  id: 'art_water_tidal_wisdom',
  name: 'Tidal Wisdom',
  type: CardType.ART,
  cost: { [ElementType.WATER]: 2 },
  rarity: Rarity.UNCOMMON,
  flavorText: 'The currents carry knowledge to those who listen.',
  targetType: 'none',
  effect: (game: GameState, playerId: number) => {
    const player = game.players[playerId];
    drawCards(game, playerId, 2);
    // Put a card back on top
    if (player.hand.length > 0) {
      const cardToReturn = player.hand.pop()!;
      player.deck.unshift(cardToReturn);
    }
  },
};

export const CRASHING_WAVE: ArtCard = {
  id: 'art_water_crashing_wave',
  name: 'Crashing Wave',
  type: CardType.ART,
  cost: { [ElementType.WATER]: 3 },
  rarity: Rarity.UNCOMMON,
  flavorText: 'The tide gives and takes in equal measure.',
  targetType: 'none',
  effect: (game: GameState, playerId: number) => {
    const opponentId = playerId === 0 ? 1 : 0;
    const opponent = game.players[opponentId];
    // Return first opponent creature to hand and gain 2 life
    if (opponent.battlefield.creatures.length > 0) {
      const creature = opponent.battlefield.creatures[0];
      opponent.battlefield.creatures.shift();
      opponent.hand.push(creature.cardId);
    }
    healCommander(game, playerId, 2);
  },
};

export const TSUNAMI: ArtCard = {
  id: 'art_water_tsunami',
  name: 'Tsunami',
  type: CardType.ART,
  cost: { [ElementType.WATER]: 5 },
  rarity: Rarity.RARE,
  flavorText: 'When the ocean speaks, all must listen.',
  targetType: 'none',
  effect: (game: GameState, playerId: number) => {
    // Return all creatures to hands
    for (const player of game.players) {
      while (player.battlefield.creatures.length > 0) {
        const creature = player.battlefield.creatures.pop()!;
        player.hand.push(creature.cardId);
      }
    }
    healCommander(game, playerId, 5);
  },
};

// MANA SOURCES
export const CRYSTALLINE_LAKE: ManaSourceCard = {
  id: 'mana_water_crystalline_lake',
  name: 'Crystalline Lake',
  type: CardType.MANA_SOURCE,
  rarity: Rarity.COMMON,
  elementType: ElementType.WATER,
  manaGenerated: { [ElementType.WATER]: 1 },
  flavorText: 'Waters so pure and clear, they shimmer like precious gems.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/CrystallineLake.png',
};

export const MYSTIC_SPRING: ManaSourceCard = {
  id: 'mana_water_mystic_spring',
  name: 'Mystic Spring',
  type: CardType.MANA_SOURCE,
  cost: { [ElementType.WATER]: 1 },
  rarity: Rarity.UNCOMMON,
  elementType: ElementType.WATER,
  manaGenerated: { [ElementType.WATER]: 1 },
  flavorText: 'Knowledge flows like water, sometimes at a cost.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/MysticSpring.png',
  abilities: [
    {
      id: 'spring_draw',
      description: 'You may pay 1 life to draw a card when tapping this.',
      effect: (game: GameState, playerId: number) => {
        const player = game.players[playerId];
        if (player.commander.currentHealth > 1) {
          createBinaryChoice(
            game,
            playerId,
            'Mystic Spring',
            'Pay 1 life to draw a card?',
            () => {
              // Yes - pay life and draw
              player.commander.currentHealth -= 1;
              drawCards(game, playerId, 1);
            },
            () => {
              // No - just generate mana
            },
            'Pay & Draw',
            'Skip',
            'mana_water_mystic_spring'
          );
        }
      },
    },
  ],
};

export const TIDAL_POOL: ManaSourceCard = {
  id: 'mana_water_tidal_pool',
  name: 'Tidal Pool',
  type: CardType.MANA_SOURCE,
  cost: { [ElementType.WATER]: 1 },
  rarity: Rarity.UNCOMMON,
  elementType: ElementType.WATER,
  manaGenerated: { [ElementType.WATER]: 1 },
  flavorText: 'Abundance feeds abundance.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/TidalPool.png',
  abilities: [
    {
      id: 'pool_bonus',
      description: 'If you have 4 or more cards in hand, gain 1 additional Water Mana.',
      effect: (game: GameState, playerId: number) => {
        const player = game.players[playerId];
        if (player.hand.length >= 4) {
          addMana(player.manaPool, { [ElementType.WATER]: 1 });
        }
      },
    },
  ],
};

// Water Deck Definition
export const WATER_DECK: DeckDefinition = {
  name: 'Water Starter Deck',
  elementType: ElementType.WATER,
  commanderId: 'cmd_water_nic',
  cards: [
    { cardId: 'cre_water_tide_sprite', count: 3 },
    { cardId: 'cre_water_silverscale_scout', count: 3 },
    { cardId: 'cre_water_river_healer', count: 2 },
    { cardId: 'cre_water_coral_guardian', count: 2 },
    { cardId: 'cre_water_moonlit_serpent', count: 2 },
    { cardId: 'cre_water_leviathan', count: 1 },
    { cardId: 'art_water_refreshing_rain', count: 3 },
    { cardId: 'art_water_flowing_grace', count: 3 },
    { cardId: 'art_water_tidal_wisdom', count: 2 },
    { cardId: 'art_water_crashing_wave', count: 2 },
    { cardId: 'art_water_tsunami', count: 1 },
    { cardId: 'mana_water_crystalline_lake', count: 1 },
    { cardId: 'mana_water_mystic_spring', count: 1 },
    { cardId: 'mana_water_tidal_pool', count: 1 },
  ],
};

// ============================================================================
// EARTH DECK - Carma
// ============================================================================

// COMMANDER
export const CARMA: CommanderCard = {
  id: 'cmd_earth_carma',
  name: 'Carma',
  type: CardType.COMMANDER,
  pronouns: 'She/They',
  subtitle: 'Game Master - Green Fae Warlock',
  health: 30,
  elementType: ElementType.EARTH,
  flavorText: "I'm here to make you feel your feelings. Yes, all of them.",
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/CardFrontCarma.png',
  abilities: [
    {
      id: 'carma_mana',
      cost: {},
      tapCost: true,
      description: 'TAP: Gain 1 Earth Mana',
      effect: (game: GameState, playerId: number) => {
        addMana(game.players[playerId].manaPool, { [ElementType.EARTH]: 1 });
      },
    },
    {
      id: 'carma_defense',
      cost: { [ElementType.EARTH]: 1 },
      tapCost: true,
      description: 'TAP + 1 Earth: Target creature gains +0/+3 until end of turn',
      effect: (game: GameState, playerId: number) => {
        const player = game.players[playerId];
        if (player.battlefield.creatures.length > 0) {
          requestTargets(
            game,
            {
              playerId,
              title: 'Carma - Defensive Boost',
              description: 'Choose a creature to give +0/+3 until end of turn',
              targetType: 'creature',
              filter: (creatureId, g) => {
                const creature = findCreatureOnBattlefield(g, creatureId);
                return creature ? creature.ownerId === playerId : false;
              },
            },
            (selection: TargetSelection) => {
              if (selection.creatures && selection.creatures.length > 0) {
                modifyCreaturePower(game, selection.creatures[0], 0, 3);
                logEvent(game, 'ability_activated', 'Carma grants +0/+3!');
              }
            },
            () => {
              // Cancel - do nothing
            }
          );
        }
      },
    },
    {
      id: 'carma_pump',
      cost: { [ElementType.EARTH]: 2 },
      tapCost: true,
      description: 'TAP + 2 Earth: All creatures you control gain +1/+1 until end of turn. Heal 2 damage from your GM.',
      effect: (game: GameState, playerId: number) => {
        const player = game.players[playerId];
        player.battlefield.creatures.forEach((creature) => {
          modifyCreaturePower(game, creature.instanceId, 1, 1);
        });
        healCommander(game, playerId, 2);
      },
    },
    {
      id: 'carma_token',
      cost: { [ElementType.EARTH]: 3 },
      description: 'Put a 2/2 Vine Panther creature token into play with Block',
      effect: (game: GameState, playerId: number) => {
        createToken(game, playerId, 'Vine_Panther', 2, 2);
        // Add Block keyword to the token
        const player = game.players[playerId];
        const token = player.battlefield.creatures[player.battlefield.creatures.length - 1];
        if (token && !token.keywords.includes(AbilityKeyword.BLOCK)) {
          token.keywords.push(AbilityKeyword.BLOCK);
        }
      },
    },
  ],
};

// CREATURES
export const ROOTKIN_DEFENDER: CreatureCard = {
  id: 'cre_earth_rootkin',
  name: 'Rootkin Defender',
  type: CardType.CREATURE,
  cost: { [ElementType.EARTH]: 2 },
  power: 1,
  health: 4,
  rarity: Rarity.COMMON,
  subtype: 'Elemental, Plant',
  keywords: [AbilityKeyword.BLOCK],
  flavorText: 'Roots run deep, and so does loyalty.',
};

export const GROVE_GUARDIAN: CreatureCard = {
  id: 'cre_earth_grove_guardian',
  name: 'Grove Guardian',
  type: CardType.CREATURE,
  cost: { [ElementType.EARTH]: 3 },
  power: 3,
  health: 4,
  rarity: Rarity.UNCOMMON,
  subtype: 'Elemental, Plant',
  keywords: [AbilityKeyword.BLOCK],
  flavorText: 'The forest protects those who protect it.',
};

export const THORNBACK_BEAR: CreatureCard = {
  id: 'cre_earth_thornback',
  name: 'Thornback Bear',
  type: CardType.CREATURE,
  cost: { [ElementType.EARTH]: 3 },
  power: 3,
  health: 3,
  rarity: Rarity.UNCOMMON,
  subtype: 'Beast',
  keywords: [],
  flavorText: "The forest's fury made flesh and fang.",
  abilities: [
    {
      id: 'thornback_retaliate',
      description: 'When this creature is attacked, it deals 1 damage to the attacker.',
      trigger: 'damage',
      effect: (game: GameState, creatureId: string) => {
        // Check if this creature received damage from other creatures this turn
        const damageReceived = getDamageReceivedByCreature(game, creatureId);

        // Retaliate against each creature that damaged this one
        for (const damageEvent of damageReceived) {
          if (damageEvent.sourceId && damageEvent.amount > 0) {
            // Deal 1 damage back to the attacker
            dealDamage(game, damageEvent.sourceId, 'creature', 1, creatureId);
            logEvent(game, 'ability_activated', 'Thornback Bear retaliates for 1 damage!');
          }
        }
      },
    },
  ],
};

export const MIRE_DRUID: CreatureCard = {
  id: 'cre_earth_mire_druid',
  name: 'Mire Druid',
  type: CardType.CREATURE,
  cost: { [ElementType.EARTH]: 4 },
  power: 2,
  health: 6,
  rarity: Rarity.RARE,
  subtype: 'Elf, Druid',
  keywords: [],
  flavorText: 'The swamp provides, endlessly.',
  abilities: [
    {
      id: 'mire_modal',
      description: 'When this creature enters play, it gains one of the following: Block, Flying, +3/-2, +0/+1, or TAP: Gain 2 Earth Mana.',
      trigger: 'enter',
      effect: (game: GameState, creatureId: string) => {
        const creature = findCreatureOnBattlefield(game, creatureId);
        if (!creature) return;

        createChooseOneModal(
          game,
          creature.ownerId,
          'Mire Druid',
          'Choose one ability to gain:',
          [
            { id: 'block', label: 'Block', description: 'Gain Block keyword' },
            { id: 'flying', label: 'Flying', description: 'Gain Flying keyword' },
            { id: 'power', label: '+3/-2', description: 'Gain +3 power and -2 health (5/4)' },
            { id: 'health', label: '+0/+1', description: 'Gain +1 health (2/7)' },
            { id: 'mana', label: 'TAP: Gain 2 Earth Mana', description: 'Adds tap ability to generate mana' },
          ],
          (selectedId: string) => {
            const creature = findCreatureOnBattlefield(game, creatureId);
            if (!creature) return;

            switch (selectedId) {
              case 'block':
                if (!creature.keywords.includes(AbilityKeyword.BLOCK)) {
                  creature.keywords.push(AbilityKeyword.BLOCK);
                  logEvent(game, 'ability_activated', 'Mire Druid gains Block!');
                }
                break;

              case 'flying':
                if (!creature.keywords.includes(AbilityKeyword.FLYING)) {
                  creature.keywords.push(AbilityKeyword.FLYING);
                  logEvent(game, 'ability_activated', 'Mire Druid gains Flying!');
                }
                break;

              case 'power':
                modifyCreaturePower(game, creatureId, 3, -2);
                logEvent(game, 'ability_activated', 'Mire Druid gains +3/-2!');
                break;

              case 'health':
                modifyCreaturePower(game, creatureId, 0, 1);
                logEvent(game, 'ability_activated', 'Mire Druid gains +0/+1!');
                break;

              case 'mana': {
                // Add a tap-activated ability to the creature
                const tapAbility: CreatureAbility = {
                  id: 'mire_tap_mana',
                  description: 'TAP: Gain 2 Earth Mana.',
                  trigger: 'activated',
                  tapCost: true,
                  effect: (game: GameState, creatureId: string) => {
                    const creature = findCreatureOnBattlefield(game, creatureId);
                    if (creature) {
                      addMana(game.players[creature.ownerId].manaPool, { [ElementType.EARTH]: 2 });
                      logEvent(game, 'ability_activated', 'Mire Druid generates 2 Earth Mana!');
                    }
                  },
                };
                if (!creature.copiedAbilities) {
                  creature.copiedAbilities = [];
                }
                creature.copiedAbilities.push(tapAbility);
                logEvent(game, 'ability_activated', 'Mire Druid gains mana generation ability!');
                break;
              }
            }
          },
          'cre_earth_mire_druid'
        );
      },
    },
  ],
};

export const ANCIENT_TREANT: CreatureCard = {
  id: 'cre_earth_treant',
  name: 'Ancient Treant',
  type: CardType.CREATURE,
  cost: { [ElementType.EARTH]: 5 },
  power: 6,
  health: 6,
  rarity: Rarity.RARE,
  subtype: 'Treant, Plant',
  keywords: [AbilityKeyword.BLOCK],
  flavorText: 'Centuries of growth, impossible to fell.',
};

// ARTS
export const NOURISH: ArtCard = {
  id: 'art_earth_nourish',
  name: 'Nourish',
  type: CardType.ART,
  cost: { [ElementType.EARTH]: 1 },
  rarity: Rarity.COMMON,
  flavorText: 'The earth gives what you need.',
  effect: (game: GameState, playerId: number) => {
    healCommander(game, playerId, 2);
  },
  targetType: 'none',
};

export const OVERGROWTH: ArtCard = {
  id: 'art_earth_overgrowth',
  name: 'Overgrowth',
  type: CardType.ART,
  cost: { [ElementType.EARTH]: 2 },
  rarity: Rarity.COMMON,
  flavorText: 'Life finds a way, always.',
  effect: (game: GameState, playerId: number) => {
    createToken(game, playerId, 'Sapling', 2, 2);
  },
  targetType: 'none',
};

export const ENTANGLE: ArtCard = {
  id: 'art_earth_entangle',
  name: 'Entangle',
  type: CardType.ART,
  cost: { [ElementType.EARTH]: 3 },
  rarity: Rarity.UNCOMMON,
  flavorText: 'The vines remember those who trespass.',
  effect: (game: GameState, playerId: number) => {
    requestTargets(
      game,
      {
        playerId,
        title: 'Entangle',
        description: 'Target creature can\'t attack or use tap abilities until end of round',
        targetType: 'creature',
      },
      (selection: TargetSelection) => {
        if (selection.creatures && selection.creatures.length > 0) {
          const creatureId = selection.creatures[0];
          const creature = findCreatureOnBattlefield(game, creatureId);

          if (creature) {
            // Tap the creature
            creature.isTapped = true;

            // Add ongoing effects to prevent attacking and tapping
            addOngoingEffect(game, {
              type: OngoingEffectType.CANT_ATTACK,
              duration: OngoingEffectDuration.END_OF_ROUND,
              targetId: creatureId,
              targetType: 'creature',
              createdByPlayerId: playerId,
            });

            addOngoingEffect(game, {
              type: OngoingEffectType.CANT_TAP,
              duration: OngoingEffectDuration.END_OF_ROUND,
              targetId: creatureId,
              targetType: 'creature',
              createdByPlayerId: playerId,
            });

            logEvent(game, 'ability_activated', 'Creature entangled! Can\'t attack or tap until end of round.');
          }
        }
      }
    );
  },
  targetType: 'creature',
  targetCount: 1,
};

export const VERDANT_RENEWAL: ArtCard = {
  id: 'art_earth_renewal',
  name: 'Verdant Renewal',
  type: CardType.ART,
  cost: { [ElementType.EARTH]: 3 },
  rarity: Rarity.UNCOMMON,
  flavorText: 'Death is just another season.',
  effect: (game: GameState, playerId: number) => {
    const player = game.players[playerId];
    // Return a creature from graveyard if available
    if (player.discard.length > 0) {
      const creatureCards = player.discard.filter((cardId) => {
        const card = game.cardInstances[cardId];
        return card && card.type === CardType.CREATURE;
      });
      if (creatureCards.length > 0) {
        // Let player choose which creature to return
        createChooseOneModal(
          game,
          playerId,
          'Verdant Renewal',
          'Choose a creature to return from your graveyard to your hand:',
          creatureCards.map((cardId, index) => {
            const card = game.cardInstances[cardId];
            return {
              id: `${cardId}_${index}`, // Make unique by adding index
              label: card?.name || cardId,
              description: card && 'power' in card ? `${card.power}/${card.health}` : '',
              cardId, // Store the actual cardId for the callback
            };
          }),
          (selectedId: string) => {
            // Extract the actual cardId from the selection
            const selectedOption = creatureCards.find((cardId, index) => `${cardId}_${index}` === selectedId);
            const cardIdToReturn = selectedOption || creatureCards[0];
            returnCardFromGraveyard(game, playerId, cardIdToReturn);
            logEvent(game, 'ability_activated', `${game.cardInstances[cardIdToReturn]?.name || 'Creature'} returned from graveyard!`);
          }
        );
      }
    }
    healCommander(game, playerId, 2);
  },
  targetType: 'none',
};

export const NATURES_WRATH: ArtCard = {
  id: 'art_earth_wrath',
  name: "Nature's Wrath",
  type: CardType.ART,
  cost: { [ElementType.EARTH]: 5 },
  rarity: Rarity.RARE,
  flavorText: 'All things return to the earth, in time.',
  effect: (game: GameState, playerId: number) => {
    requestTargets(
      game,
      {
        playerId,
        title: "Nature's Wrath",
        description: 'Destroy target creature',
        targetType: 'creature',
      },
      (selection: TargetSelection) => {
        if (selection.creatures && selection.creatures.length > 0) {
          destroyCreature(game, selection.creatures[0]);
        }
      }
    );
  },
  targetType: 'creature',
  targetCount: 1,
};

// MANA SOURCES
export const VERDANT_GROVE: ManaSourceCard = {
  id: 'mana_earth_glade',
  name: 'Verdant Grove',
  type: CardType.MANA_SOURCE,
  rarity: Rarity.COMMON,
  elementType: ElementType.EARTH,
  manaGenerated: { [ElementType.EARTH]: 1 },
  flavorText: 'The forest is as unknowable as it is beautiful.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/VerdantGrove.png',
};

export const HEALING_SPRING: ManaSourceCard = {
  id: 'mana_earth_spring',
  name: 'Healing Garden',
  type: CardType.MANA_SOURCE,
  cost: { [ElementType.EARTH]: 1 },
  rarity: Rarity.UNCOMMON,
  elementType: ElementType.EARTH,
  manaGenerated: { [ElementType.EARTH]: 1 },
  flavorText: 'Life springs eternal from the deep earth.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/HealingGarden.png',
  abilities: [
    {
      id: 'spring_heal',
      description: 'When tapped, heal 1 damage from your GM',
      effect: (game: GameState, playerId: number) => {
        healCommander(game, playerId, 1);
      },
    },
  ],
};

export const FERTILE_SOIL: ManaSourceCard = {
  id: 'mana_earth_soil',
  name: 'Fertile Soil',
  type: CardType.MANA_SOURCE,
  cost: { [ElementType.EARTH]: 1 },
  rarity: Rarity.UNCOMMON,
  elementType: ElementType.EARTH,
  manaGenerated: { [ElementType.EARTH]: 1 },
  flavorText: 'Abundance breeds abundance.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/FertileSoil.png',
  abilities: [
    {
      id: 'soil_bonus',
      description: 'If you control 3 or more creatures, gain 1 additional Earth Mana.',
      effect: (game: GameState, playerId: number) => {
        const player = game.players[playerId];
        if (player.battlefield.creatures.length >= 3) {
          addMana(player.manaPool, { [ElementType.EARTH]: 1 });
        }
      },
    },
  ],
};

export const WORLDROOT_GROVE: ManaSourceCard = {
  id: 'mana_earth_worldroot',
  name: 'Worldroot Grove',
  type: CardType.MANA_SOURCE,
  cost: { [ElementType.EARTH]: 1 },
  rarity: Rarity.RARE,
  elementType: ElementType.EARTH,
  manaGenerated: { [ElementType.EARTH]: 2 },
  flavorText: 'The heart of the forest beats eternal.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/WorldrootGrove.png',
  abilities: [
    {
      id: 'worldroot_buff',
      description: 'Target creature gets +0/+2 until end of turn.',
      effect: (game: GameState, playerId: number) => {
        const player = game.players[playerId];
        if (player.battlefield.creatures.length > 0) {
          requestTargets(
            game,
            {
              playerId,
              title: 'Worldroot Grove',
              description: 'Choose a creature to give +0/+2 until end of turn',
              targetType: 'creature',
              filter: (creatureId, g) => {
                const creature = findCreatureOnBattlefield(g, creatureId);
                return creature ? creature.ownerId === playerId : false;
              },
            },
            (selection: TargetSelection) => {
              if (selection.creatures && selection.creatures.length > 0) {
                modifyCreaturePower(game, selection.creatures[0], 0, 2);
                logEvent(game, 'ability_activated', 'Worldroot Grove grants +0/+2!');
              }
            },
            () => {
              // Cancel - do nothing
            }
          );
        }
      },
    },
  ],
};

export const EARTH_DECK: DeckDefinition = {
  name: 'Earth Starter Deck',
  elementType: ElementType.EARTH,
  commanderId: 'cmd_earth_carma',
  cards: [
    { cardId: 'cre_earth_rootkin', count: 3 },
    { cardId: 'cre_earth_grove_guardian', count: 2 },
    { cardId: 'cre_earth_thornback', count: 2 },
    { cardId: 'cre_earth_mire_druid', count: 1 },
    { cardId: 'cre_earth_treant', count: 1 },
    { cardId: 'art_earth_nourish', count: 3 },
    { cardId: 'art_earth_overgrowth', count: 3 },
    { cardId: 'art_earth_entangle', count: 2 },
    { cardId: 'art_earth_renewal', count: 2 },
    { cardId: 'art_earth_wrath', count: 1 },
    { cardId: 'mana_earth_glade', count: 2 },
    { cardId: 'mana_earth_spring', count: 1 },
    { cardId: 'mana_earth_soil', count: 1 },
    { cardId: 'mana_earth_worldroot', count: 1 },
  ],
};

// ============================================================================
// DARK DECK - Neal
// ============================================================================

// COMMANDER
export const NEAL: CommanderCard = {
  id: 'cmd_dark_neal',
  name: 'Neal',
  type: CardType.COMMANDER,
  pronouns: 'He/Him',
  subtitle: 'Game Master - Tiefling Druid',
  health: 30,
  elementType: ElementType.DARK,
  flavorText: "This is your story; I'm just the narrator.",
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/CardFrontNeal.png',
  abilities: [
    {
      id: 'neal_draw_discard',
      cost: {},
      tapCost: true,
      description: 'TAP: Draw a card, then discard a card',
      effect: (game: GameState, playerId: number) => {
        // Draw a card first
        drawCards(game, playerId, 1);

        const player = game.players[playerId];

        // If player has no cards, nothing to discard
        if (player.hand.length === 0) return;

        // Create modal to choose which card to discard
        const handOptions = player.hand.map((cardId, index) => {
          const card = game.cardInstances[cardId];
          return {
            id: `${cardId}_${index}`, // Make unique by adding index
            label: card?.name || cardId,
            description: card ? `${card.type}` : '',
            cardId, // Store the actual cardId for the callback
          };
        });

        createChooseOneModal(
          game,
          playerId,
          'Discard a Card',
          'Choose a card to discard:',
          handOptions,
          (selectedId: string) => {
            // Extract the cardId from the option that was selected
            const selectedOption = handOptions.find((opt) => opt.id === selectedId);
            if (!selectedOption) return;

            // Remove the selected card from hand and put in discard
            const cardIndex = player.hand.indexOf(selectedOption.cardId);
            if (cardIndex !== -1) {
              player.hand.splice(cardIndex, 1);
              player.discard.push(selectedOption.cardId);
              logEvent(game, 'ability_activated', `Discarded ${game.cardInstances[selectedOption.cardId]?.name || 'a card'}`);
            }
          }
        );
      },
    },
    {
      id: 'neal_sacrifice',
      cost: { [ElementType.DARK]: 1 },
      tapCost: true,
      description: 'TAP + 🌑: Sacrifice one of your own creatures. You gain life equal to its Health.',
      effect: (game: GameState, playerId: number) => {
        const player = game.players[playerId];
        if (player.battlefield.creatures.length > 0) {
          const creatureToSac = player.battlefield.creatures[0];
          const health = creatureToSac.health;
          sacrificeCreature(game, creatureToSac.instanceId);
          healCommander(game, playerId, health);
        }
      },
    },
    {
      id: 'neal_token',
      cost: { [ElementType.DARK]: 2 },
      tapCost: true,
      description: 'TAP + 🌑🌑: Put a 1/1 Demon Cat creature token into play.',
      effect: (game: GameState, playerId: number) => {
        createToken(game, playerId, 'Demon Cat', 1, 1);
      },
    },
    {
      id: 'neal_reanimate',
      cost: { [ElementType.DARK]: 3 },
      tapCost: true,
      description: 'TAP + 🌑🌑🌑: Return target creature from your graveyard to your field with -1/-1.',
      effect: (game: GameState, playerId: number) => {
        const player = game.players[playerId];
        // Get last creature from graveyard
        if (player.discard.length > 0) {
          // Find first creature card in discard
          const creatureIndex = player.discard.findIndex((cardId) => {
            const card = game.cardInstances[cardId];
            return card && card.type === CardType.CREATURE;
          });

          if (creatureIndex !== -1) {
            const cardId = player.discard[creatureIndex];
            player.discard.splice(creatureIndex, 1);

            // Create instance on battlefield
            const instanceId = `${cardId}_${Date.now()}_${Math.random()}`;
            const card = game.cardInstances[cardId] as CreatureCard;
            player.battlefield.creatures.push({
              instanceId,
              cardId,
              ownerId: playerId,
              power: card.power - 1,
              health: card.health - 1,
              maxHealth: card.health - 1,
              currentHealth: card.health - 1,
              isTapped: false,
              summoningSick: true,
              keywords: [...(card.keywords || [])],
              counters: { plusOnePlusOne: 0, minusOneMinusOne: 0 },
              activeAbilities: [],
            });
          }
        }
      },
    },
  ],
};

// CREATURES
export const SHADOW_IMP: CreatureCard = {
  id: 'cre_dark_shadow_imp',
  name: 'Shadow Imp',
  type: CardType.CREATURE,
  cost: { [ElementType.DARK]: 1 },
  power: 1,
  health: 1,
  rarity: Rarity.COMMON,
  subtype: 'Demon, Imp',
  keywords: [],
  flavorText: 'Even in death, they serve.',
  abilities: [
    {
      id: 'shadow_imp_death',
      description: 'When this creature is defeated, you gain 1 life.',
      trigger: 'defeat',
      effect: (game: GameState, creatureId: string) => {
        const creature = game.players.flatMap((p) => p.battlefield.creatures).find((c) => c.instanceId === creatureId);
        if (creature) {
          healCommander(game, creature.ownerId, 1);
        }
      },
    },
  ],
};

export const DUSK_STALKER: CreatureCard = {
  id: 'cre_dark_dusk_stalker',
  name: 'Dusk Stalker',
  type: CardType.CREATURE,
  cost: { [ElementType.DARK]: 2 },
  power: 2,
  health: 1,
  rarity: Rarity.COMMON,
  subtype: 'Rogue, Spirit',
  keywords: [],
  flavorText: 'The dark feeds on more than fear.',
  abilities: [
    {
      id: 'dusk_stalker_enter',
      description: 'When this creature enters play, target player loses 1 life.',
      trigger: 'enter',
      effect: (game: GameState, creatureId: string) => {
        const creature = game.players.flatMap((p) => p.battlefield.creatures).find((c) => c.instanceId === creatureId);
        if (creature) {
          const opponentId = getOpponentId(creature.ownerId);
          dealDamage(game, `player_${opponentId}`, 'player', 1);
        }
      },
    },
  ],
};

export const GRAVE_TENDER: CreatureCard = {
  id: 'cre_dark_grave_tender',
  name: 'Grave Tender',
  type: CardType.CREATURE,
  cost: { [ElementType.DARK]: 2 },
  power: 1,
  health: 3,
  rarity: Rarity.UNCOMMON,
  subtype: 'Undead',
  keywords: [],
  flavorText: 'Death is just a transition.',
};

export const VOID_WRAITH: CreatureCard = {
  id: 'cre_dark_void_wraith',
  name: 'Void Wraith',
  type: CardType.CREATURE,
  cost: { [ElementType.DARK]: 2 },
  power: 3,
  health: 2,
  rarity: Rarity.UNCOMMON,
  subtype: 'Undead, Wraith',
  keywords: [],
  flavorText: 'It hungers for what once was.',
  abilities: [
    {
      id: 'void_mana',
      description: 'TAP: Gain 1 Dark Mana. If any creature was defeated this turn, gain 1 additional Dark Mana.',
      trigger: 'activated',
      tapCost: true,
      effect: (game: GameState, creatureId: string) => {
        const creature = game.players.flatMap(p => p.battlefield.creatures).find(c => c.instanceId === creatureId);
        if (!creature) return;

        const player = game.players[creature.ownerId];

        // Base mana
        addMana(player.manaPool, { [ElementType.DARK]: 1 });

        // Bonus if creature defeated this turn
        if (game.creaturesDefeatedThisTurn && game.creaturesDefeatedThisTurn.length > 0) {
          addMana(player.manaPool, { [ElementType.DARK]: 1 });
        }
      },
    },
  ],
};

export const LUCIFER_SHADOW_PRINCE: CreatureCard = {
  id: 'cre_dark_lucifer',
  name: 'Lucifer, Shadow Prince',
  type: CardType.CREATURE,
  cost: { [ElementType.DARK]: 3 },
  power: 2,
  health: 3,
  rarity: Rarity.RARE,
  subtype: 'Demon Cat',
  keywords: [],
  flavorText: 'The firstborn of darkness, proud and unyielding.',
};

export const LILITH_NIGHTS_WHISPER: CreatureCard = {
  id: 'cre_dark_lilith',
  name: "Lilith, Night's Whisper",
  type: CardType.CREATURE,
  cost: { [ElementType.DARK]: 3 },
  power: 3,
  health: 2,
  rarity: Rarity.RARE,
  subtype: 'Demon Cat',
  keywords: [],
  flavorText: 'She who walks between worlds.',
};

export const BAPHOMET_DREAMING_SHADOW: CreatureCard = {
  id: 'cre_dark_baphomet',
  name: 'Baphomet, Dreaming Shadow',
  type: CardType.CREATURE,
  cost: { [ElementType.DARK]: 3 },
  power: 2,
  health: 2,
  rarity: Rarity.RARE,
  subtype: 'Demon Cat',
  keywords: [],
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/BaphometDreamingShadow.png',
  abilities: [
    {
      id: 'baphomet_trio',
      description: 'When this creature enters play, if you control both Lucifer, Shadow Prince and Lilith, Night\'s Whisper, gain 3 Dark Mana.',
      trigger: 'enter',
      effect: (game: GameState, creatureId: string) => {
        const creature = game.players.flatMap((p) => p.battlefield.creatures).find((c) => c.instanceId === creatureId);
        if (creature) {
          const player = game.players[creature.ownerId];
          // Check if player controls Lucifer and Lilith
          const hasLucifer = player.battlefield.creatures.some(c => c.cardId === 'cre_dark_lucifer');
          const hasLilith = player.battlefield.creatures.some(c => c.cardId === 'cre_dark_lilith');

          if (hasLucifer && hasLilith) {
            addMana(player.manaPool, { [ElementType.DARK]: 3 });
          }
        }
      },
    },
  ],
  flavorText: 'In the realm between sleep and waking, Baphomet weaves nightmares into reality.',
};

// ARTS
export const DARK_PACT: ArtCard = {
  id: 'art_dark_pact',
  name: 'Dark Pact',
  type: CardType.ART,
  cost: { [ElementType.DARK]: 1 },
  rarity: Rarity.COMMON,
  flavorText: 'Every deal has a price.',
  effect: (game: GameState, playerId: number) => {
    const player = game.players[playerId];
    // Sacrifice first creature if any
    if (player.battlefield.creatures.length > 0) {
      const creatureToSac = player.battlefield.creatures[0];
      sacrificeCreature(game, creatureToSac.instanceId);
    }
    drawCards(game, playerId, 2);
  },
  targetType: 'none',
};

export const LIFE_DRAIN: ArtCard = {
  id: 'art_dark_life_drain',
  name: 'Life Drain',
  type: CardType.ART,
  cost: { [ElementType.DARK]: 1 },
  rarity: Rarity.UNCOMMON,
  flavorText: "What's yours becomes mine.",
  effect: (game: GameState, playerId: number) => {
    requestTargets(
      game,
      {
        playerId,
        title: 'Life Drain',
        description: 'Deal 2 damage to target player and heal 2',
        targetType: 'player',
      },
      (selection: TargetSelection) => {
        if (selection.players && selection.players.length > 0) {
          const targetPlayerId = selection.players[0];
          dealDamage(game, `player_${targetPlayerId}`, 'player', 2);
          healCommander(game, playerId, 2);
        }
      }
    );
  },
  targetType: 'player',
  targetCount: 1,
};

export const SHADOW_BOLT: ArtCard = {
  id: 'art_dark_shadow_bolt',
  name: 'Shadow Bolt',
  type: CardType.ART,
  cost: { [ElementType.DARK]: 2 },
  rarity: Rarity.UNCOMMON,
  flavorText: 'The darkness takes the weak first.',
  effect: (game: GameState, playerId: number) => {
    requestTargets(
      game,
      {
        playerId,
        title: 'Shadow Bolt',
        description: 'Destroy target creature with power 2 or less',
        targetType: 'creature',
        filter: (targetId: string, game: GameState) => {
          const creature = findCreatureOnBattlefield(game, targetId);
          return creature !== null && creature.power <= 2;
        },
      },
      (selection: TargetSelection) => {
        if (selection.creatures && selection.creatures.length > 0) {
          destroyCreature(game, selection.creatures[0]);
        }
      }
    );
  },
  targetType: 'creature',
  targetCount: 1,
};

export const NECROMANTIC_RITUAL: ArtCard = {
  id: 'art_dark_ritual',
  name: 'Necromantic Ritual',
  type: CardType.ART,
  cost: { [ElementType.DARK]: 4 },
  rarity: Rarity.RARE,
  flavorText: 'Death is merely a doorway.',
  effect: (game: GameState, playerId: number) => {
    // Try to find a creature in any graveyard (prefer opponent's)
    const opponentId = getOpponentId(playerId);
    const opponent = game.players[opponentId];
    const player = game.players[playerId];

    let sourcePlayer = opponent;
    let creatureIndex = opponent.discard.findIndex((cardId) => {
      const card = game.cardInstances[cardId];
      return card && card.type === CardType.CREATURE;
    });

    // If no creature in opponent's graveyard, check own
    if (creatureIndex === -1) {
      sourcePlayer = player;
      creatureIndex = player.discard.findIndex((cardId) => {
        const card = game.cardInstances[cardId];
        return card && card.type === CardType.CREATURE;
      });
    }

    if (creatureIndex !== -1) {
      const cardId = sourcePlayer.discard[creatureIndex];
      sourcePlayer.discard.splice(creatureIndex, 1);

      // Create instance on caster's battlefield
      const instanceId = `${cardId}_${Date.now()}_${Math.random()}`;
      const card = game.cardInstances[cardId] as CreatureCard;
      player.battlefield.creatures.push({
        instanceId,
        cardId,
        ownerId: playerId,
        power: card.power,
        health: card.health,
        maxHealth: card.health,
        currentHealth: card.health,
        isTapped: false,
        summoningSick: true,
        keywords: [...(card.keywords || [])],
        counters: { plusOnePlusOne: 0, minusOneMinusOne: 0 },
        activeAbilities: [],
      });
    }
  },
  targetType: 'none',
};

// MANA SOURCES
export const SHADOWY_CAVE: ManaSourceCard = {
  id: 'mana_dark_cave',
  name: 'Shadowy Cave',
  type: CardType.MANA_SOURCE,
  rarity: Rarity.COMMON,
  elementType: ElementType.DARK,
  manaGenerated: { [ElementType.DARK]: 1 },
  flavorText: 'There is no balance without a little darkness.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/ShadowyCave.png',
};

export const BONEYARD_SHRINE: ManaSourceCard = {
  id: 'mana_dark_boneyard',
  name: 'Boneyard Shrine',
  type: CardType.MANA_SOURCE,
  cost: { [ElementType.DARK]: 1 },
  rarity: Rarity.UNCOMMON,
  elementType: ElementType.DARK,
  manaGenerated: { [ElementType.DARK]: 1 },
  flavorText: 'Where the fallen rest, power gathers.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/BoneyardShrine.png',
  abilities: [
    {
      id: 'boneyard_bonus',
      description: 'If any creature was defeated this turn, gain 1 additional Dark Mana.',
      effect: (game: GameState, playerId: number) => {
        // Check if any creatures were defeated this turn
        if (game.creaturesDefeatedThisTurn && game.creaturesDefeatedThisTurn.length > 0) {
          const player = game.players[playerId];
          addMana(player.manaPool, { [ElementType.DARK]: 1 });
        }
      },
    },
  ],
};

export const DARK_DECK: DeckDefinition = {
  name: 'Dark Starter Deck',
  elementType: ElementType.DARK,
  commanderId: 'cmd_dark_neal',
  cards: [
    { cardId: 'cre_dark_shadow_imp', count: 3 },
    { cardId: 'cre_dark_dusk_stalker', count: 3 },
    { cardId: 'cre_dark_grave_tender', count: 2 },
    { cardId: 'cre_dark_void_wraith', count: 2 },
    { cardId: 'cre_dark_lucifer', count: 1 },
    { cardId: 'cre_dark_lilith', count: 1 },
    { cardId: 'cre_dark_baphomet', count: 1 },
    { cardId: 'art_dark_pact', count: 3 },
    { cardId: 'art_dark_life_drain', count: 2 },
    { cardId: 'art_dark_shadow_bolt', count: 2 },
    { cardId: 'art_dark_ritual', count: 1 },
    { cardId: 'mana_dark_cave', count: 2 },
    { cardId: 'mana_dark_boneyard', count: 1 },
  ],
};

// ============================================================================
// LIGHT DECK - Brandy
// ============================================================================

// COMMANDER
export const BRANDY: CommanderCard = {
  id: 'cmd_light_brandy',
  name: 'Brandy',
  type: CardType.COMMANDER,
  pronouns: 'She/Her',
  subtitle: 'Game Master - Human Paladin',
  health: 30,
  elementType: ElementType.LIGHT,
  flavorText: 'By the Light we shall prevail!',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/CardFrontBrandy.png',
  abilities: [
    {
      id: 'brandy_buff',
      cost: {},
      description: 'Target creature gains +1/+1 until end of turn',
      effect: (game: GameState, playerId: number) => {
        const player = game.players[playerId];
        if (player.battlefield.creatures.length > 0) {
          requestTargets(
            game,
            {
              playerId,
              title: 'Brandy - Buff Creature',
              description: 'Choose a creature to give +1/+1 until end of turn',
              targetType: 'creature',
              filter: (creatureId, g) => {
                const creature = findCreatureOnBattlefield(g, creatureId);
                return creature ? creature.ownerId === playerId : false;
              },
            },
            (selection: TargetSelection) => {
              if (selection.creatures && selection.creatures.length > 0) {
                modifyCreaturePower(game, selection.creatures[0], 1, 1);
                logEvent(game, 'ability_activated', 'Brandy grants +1/+1!');
              }
            },
            () => {
              // Cancel - do nothing
            }
          );
        }
      },
    },
    {
      id: 'brandy_mass_buff',
      cost: { [ElementType.LIGHT]: 1 },
      description: 'All creatures you control gain +1/+1 until end of turn',
      effect: (game: GameState, playerId: number) => {
        const player = game.players[playerId];
        player.battlefield.creatures.forEach((creature) => {
          modifyCreaturePower(game, creature.instanceId, 1, 1);
        });
      },
    },
    {
      id: 'brandy_protect',
      cost: { [ElementType.LIGHT]: 2 },
      description: 'Prevent all damage that would be dealt to creatures you control this turn',
      effect: (game: GameState, playerId: number) => {
        // Add prevention shields to all your creatures
        const player = game.players[playerId];
        player.battlefield.creatures.forEach((creature) => {
          // Add a large shield (effectively preventing all damage this turn)
          addPreventionShield(game, creature.instanceId, 'creature', 999);
        });
      },
    },
    {
      id: 'brandy_removal',
      cost: { [ElementType.LIGHT]: 3 },
      description: 'Choose one: Destroy target creature with power 3 or greater, or gain 3 life.',
      effect: (game: GameState, playerId: number) => {
        createChooseOneModal(
          game,
          playerId,
          'Divine Judgment',
          'Choose one:',
          [
            { id: 'destroy', label: 'Destroy creature', description: 'Destroy target creature with power 3 or greater' },
            { id: 'heal', label: 'Gain 3 life', description: 'Heal your commander for 3' },
          ],
          (selectedId: string) => {
            if (selectedId === 'destroy') {
              // Request target selection for creature with power 3+
              requestTargets(
                game,
                {
                  playerId,
                  title: 'Divine Judgment',
                  description: 'Destroy target creature with power 3 or greater',
                  targetType: 'creature',
                  filter: (targetId: string, game: GameState) => {
                    const target = findCreatureOnBattlefield(game, targetId);
                    return target !== null && target.power >= 3;
                  },
                },
                (selection: TargetSelection) => {
                  if (selection.creatures && selection.creatures.length > 0) {
                    destroyCreature(game, selection.creatures[0]);
                  }
                }
              );
            } else {
              // Heal
              healCommander(game, playerId, 3);
            }
          },
          'brandy'
        );
      },
    },
  ],
};

// CREATURES
export const DAWN_SCOUT: CreatureCard = {
  id: 'cre_light_dawn_scout',
  name: 'Dawn Scout',
  type: CardType.CREATURE,
  cost: { [ElementType.LIGHT]: 1 },
  power: 2,
  health: 1,
  rarity: Rarity.COMMON,
  subtype: 'Human Scout',
  keywords: [],
  flavorText: 'First light brings the brave.',
};

export const ASPIRING_SQUIRE: CreatureCard = {
  id: 'cre_light_squire',
  name: 'Aspiring Squire',
  type: CardType.CREATURE,
  cost: { [ElementType.LIGHT]: 2 },
  power: 2,
  health: 2,
  rarity: Rarity.UNCOMMON,
  subtype: 'Human Warrior',
  keywords: [],
  flavorText: 'Faith makes them stronger with every breath.',
  abilities: [
    {
      id: 'squire_evolve',
      description: 'When this creature attacks, roll 1d4. On a 4, upgrade this card to Crusading Knight.',
      trigger: 'attack',
      effect: (game: GameState, creatureId: string) => {
        const creature = findCreatureOnBattlefield(game, creatureId);
        if (!creature) return;

        const roll = rollDieTracked(game, 4, creature.ownerId, 'none');
        if (roll === 4) {
          // Evolution successful!
          const player = game.players[creature.ownerId];
          const creatureIndex = player.battlefield.creatures.findIndex(c => c.instanceId === creatureId);
          if (creatureIndex !== -1) {
            // Remove the squire
            player.battlefield.creatures.splice(creatureIndex, 1);
            player.discard.push(creature.cardId);

            // Create Crusading Knight in its place
            const knightCard = ALL_CARDS['cre_light_knight'] as CreatureCard;
            const newKnight: BattlefieldCreature = {
              instanceId: generateInstanceId(),
              cardId: 'cre_light_knight',
              ownerId: creature.ownerId,
              power: knightCard.power,
              health: knightCard.health,
              maxHealth: knightCard.health,
              currentHealth: knightCard.health,
              isTapped: creature.isTapped,
              summoningSick: false, // Keep the attack going
              keywords: [...(knightCard.keywords || [])],
              counters: { plusOnePlusOne: 0, minusOneMinusOne: 0 },
              activeAbilities: [],
            };
            player.battlefield.creatures.push(newKnight);
            logEvent(game, 'ability_activated', `${creature.ownerId === 0 ? 'You' : 'Opponent'} evolved Aspiring Squire into Crusading Knight!`);
          }
        }
      },
    },
  ],
};

export const HERALD_OF_DAWN: CreatureCard = {
  id: 'cre_light_herald_dawn',
  name: 'Herald of Dawn',
  type: CardType.CREATURE,
  cost: { [ElementType.LIGHT]: 3 },
  power: 1,
  health: 4,
  rarity: Rarity.UNCOMMON,
  subtype: 'Aasimar Cleric',
  keywords: [],
  flavorText: 'The morning star heralds the coming light.',
};

export const HERALD_OF_DUSK: CreatureCard = {
  id: 'cre_light_herald_dusk',
  name: 'Herald of Dusk',
  type: CardType.CREATURE,
  cost: { [ElementType.LIGHT]: 3 },
  power: 1,
  health: 4,
  rarity: Rarity.UNCOMMON,
  subtype: 'Aasimar Cleric',
  keywords: [],
  flavorText: 'The evening star promises protection through darkness.',
};

export const CRUSADING_KNIGHT: CreatureCard = {
  id: 'cre_light_knight',
  name: 'Crusading Knight',
  type: CardType.CREATURE,
  cost: { [ElementType.LIGHT]: 3 },
  power: 3,
  health: 3,
  rarity: Rarity.UNCOMMON,
  subtype: 'Human Knight',
  keywords: [],
  flavorText: 'Strike with conviction, heal with faith.',
  abilities: [
    {
      id: 'knight_evolve',
      description: 'When this creature attacks, roll 1d6. On a 6, upgrade this card to Lightbringer Paladin.',
      trigger: 'attack',
      effect: (game: GameState, creatureId: string) => {
        const creature = findCreatureOnBattlefield(game, creatureId);
        if (!creature) return;

        const roll = rollDieTracked(game, 6, creature.ownerId, 'none');
        if (roll === 6) {
          // Evolution successful!
          const player = game.players[creature.ownerId];
          const creatureIndex = player.battlefield.creatures.findIndex(c => c.instanceId === creatureId);
          if (creatureIndex !== -1) {
            // Remove the knight
            player.battlefield.creatures.splice(creatureIndex, 1);
            player.discard.push(creature.cardId);

            // Create Lightbringer Paladin in its place
            const paladinCard = ALL_CARDS['cre_light_paladin'] as CreatureCard;
            const newPaladin: BattlefieldCreature = {
              instanceId: generateInstanceId(),
              cardId: 'cre_light_paladin',
              ownerId: creature.ownerId,
              power: paladinCard.power,
              health: paladinCard.health,
              maxHealth: paladinCard.health,
              currentHealth: paladinCard.health,
              isTapped: creature.isTapped,
              summoningSick: false, // Keep the attack going
              keywords: [...(paladinCard.keywords || [])],
              counters: { plusOnePlusOne: 0, minusOneMinusOne: 0 },
              activeAbilities: [],
            };
            player.battlefield.creatures.push(newPaladin);
            logEvent(game, 'ability_activated', `${creature.ownerId === 0 ? 'You' : 'Opponent'} evolved Crusading Knight into Lightbringer Paladin!`);
          }
        }
      },
    },
  ],
};

export const CELESTIAL_AVENGER: CreatureCard = {
  id: 'cre_light_avenger',
  name: 'Celestial Avenger',
  type: CardType.CREATURE,
  cost: { [ElementType.LIGHT]: 5 },
  power: 6,
  health: 6,
  rarity: Rarity.RARE,
  subtype: 'Angel',
  keywords: [AbilityKeyword.FLYING],
  flavorText: 'When both heralds call, heaven answers.',
};

export const LIGHTBRINGER_PALADIN: CreatureCard = {
  id: 'cre_light_paladin',
  name: 'Lightbringer Paladin',
  type: CardType.CREATURE,
  cost: { [ElementType.LIGHT]: 5 },
  power: 5,
  health: 4,
  rarity: Rarity.RARE,
  subtype: 'Human Paladin',
  keywords: [],
  flavorText: 'Where light shines, darkness cannot stand.',
  abilities: [
    {
      id: 'paladin_heal',
      description: 'When this creature attacks, your GM may heal 2 damage.',
      trigger: 'attack',
      effect: (game: GameState, creatureId: string) => {
        const creature = findCreatureOnBattlefield(game, creatureId);
        if (creature) {
          createBinaryChoice(
            game,
            creature.ownerId,
            'Lightbringer Paladin',
            'Heal 2 damage?',
            () => {
              healCommander(game, creature.ownerId, 2);
            },
            () => {
              // No heal
            },
            'Heal',
            'Skip',
            'cre_light_paladin'
          );
        }
      },
    },
  ],
};

// ARTS
export const HOLY_LIGHT: ArtCard = {
  id: 'art_light_holy_light',
  name: 'Holy Light',
  type: CardType.ART,
  cost: { [ElementType.LIGHT]: 1 },
  rarity: Rarity.COMMON,
  flavorText: 'The light mends all wounds.',
  effect: (game: GameState, playerId: number) => {
    healCommander(game, playerId, 3);
  },
  targetType: 'none',
};

export const DIVINE_BLESSING: ArtCard = {
  id: 'art_light_blessing',
  name: 'Divine Blessing',
  type: CardType.ART,
  cost: { [ElementType.LIGHT]: 2 },
  rarity: Rarity.COMMON,
  flavorText: 'Stand tall in the light.',
  effect: (game: GameState, playerId: number) => {
    requestTargets(
      game,
      {
        playerId,
        title: 'Divine Blessing',
        description: 'Target creature gets +2/+2',
        targetType: 'creature',
      },
      (selection: TargetSelection) => {
        if (selection.creatures && selection.creatures.length > 0) {
          modifyCreaturePower(game, selection.creatures[0], 2, 2, 'permanent');
        }
      }
    );
  },
  targetType: 'creature',
  targetCount: 1,
};

export const RIGHTEOUS_SHIELD: ArtCard = {
  id: 'art_light_shield',
  name: 'Righteous Shield',
  type: CardType.ART,
  cost: { [ElementType.LIGHT]: 2 },
  rarity: Rarity.RARE,
  flavorText: 'Faith is the strongest armor.',
  effect: (game: GameState, playerId: number) => {
    requestTargets(
      game,
      {
        playerId,
        title: 'Righteous Shield',
        description: 'Prevent next 5 damage to target',
        targetType: 'any',
      },
      (selection: TargetSelection) => {
        if (selection.creatures && selection.creatures.length > 0) {
          addPreventionShield(game, selection.creatures[0], 'creature', 5);
        } else if (selection.players && selection.players.length > 0) {
          addPreventionShield(game, `player_${selection.players[0]}`, 'player', 5);
        }
      }
    );
  },
  targetType: 'any',
  targetCount: 1,
};

export const SMITE: ArtCard = {
  id: 'art_light_smite',
  name: 'Smite',
  type: CardType.ART,
  cost: { [ElementType.LIGHT]: 5 },
  rarity: Rarity.RARE,
  flavorText: 'Judgment falls swift and certain.',
  effect: (game: GameState, playerId: number) => {
    requestTargets(
      game,
      {
        playerId,
        title: 'Smite',
        description: 'Destroy target creature with power 3 or greater',
        targetType: 'creature',
        filter: (targetId: string, game: GameState) => {
          const creature = findCreatureOnBattlefield(game, targetId);
          return creature !== null && creature.power >= 3;
        },
      },
      (selection: TargetSelection) => {
        if (selection.creatures && selection.creatures.length > 0) {
          destroyCreature(game, selection.creatures[0]);
        }
      }
    );
  },
  targetType: 'creature',
  targetCount: 1,
};

// MANA SOURCES
export const SUNNY_BEAMS: ManaSourceCard = {
  id: 'mana_light_sunny',
  name: 'Sunny Beams',
  type: CardType.MANA_SOURCE,
  rarity: Rarity.COMMON,
  elementType: ElementType.LIGHT,
  manaGenerated: { [ElementType.LIGHT]: 1 },
  flavorText: 'The Sun is the source of all light. All life.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/SunnyBeams.png',
};

export const SACRED_TEMPLE: ManaSourceCard = {
  id: 'mana_light_temple',
  name: 'Sacred Temple',
  type: CardType.MANA_SOURCE,
  cost: { [ElementType.LIGHT]: 1 },
  rarity: Rarity.UNCOMMON,
  elementType: ElementType.LIGHT,
  manaGenerated: { [ElementType.LIGHT]: 1 },
  flavorText: 'Faith is a fortress no army can breach.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/SacredTemple.png',
  abilities: [
    {
      id: 'temple_buff',
      description: 'Target creature gets +1/+0 until end of turn.',
      effect: (game: GameState, playerId: number) => {
        const player = game.players[playerId];
        if (player.battlefield.creatures.length > 0) {
          requestTargets(
            game,
            {
              playerId,
              title: 'Sacred Temple',
              description: 'Choose a creature to give +1/+0 until end of turn',
              targetType: 'creature',
              filter: (creatureId, g) => {
                const creature = findCreatureOnBattlefield(g, creatureId);
                return creature ? creature.ownerId === playerId : false;
              },
            },
            (selection: TargetSelection) => {
              if (selection.creatures && selection.creatures.length > 0) {
                modifyCreaturePower(game, selection.creatures[0], 1, 0);
                logEvent(game, 'ability_activated', 'Sacred Temple grants +1/+0!');
              }
            },
            () => {
              // Cancel - do nothing
            }
          );
        }
      },
    },
  ],
};

export const RADIANT_SANCTUARY: ManaSourceCard = {
  id: 'mana_light_sanctuary',
  name: 'Radiant Sanctuary',
  type: CardType.MANA_SOURCE,
  cost: { [ElementType.LIGHT]: 1 },
  rarity: Rarity.UNCOMMON,
  elementType: ElementType.LIGHT,
  manaGenerated: { [ElementType.LIGHT]: 1 },
  flavorText: 'A place of peace in a world of chaos.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/RadiantSanctuary.png',
  abilities: [
    {
      id: 'sanctuary_prevent',
      description: 'Prevent 2 damage to your GM the next time they would take damage.',
      effect: (game: GameState, playerId: number) => {
        // Add prevention shield for player commander
        addPreventionShield(game, `player_${playerId}`, 'player', 2);
      },
    },
  ],
};

export const LIGHT_DECK: DeckDefinition = {
  name: 'Light Starter Deck',
  elementType: ElementType.LIGHT,
  commanderId: 'cmd_light_brandy',
  cards: [
    { cardId: 'cre_light_dawn_scout', count: 3 },
    { cardId: 'cre_light_squire', count: 2 },
    { cardId: 'cre_light_herald_dawn', count: 2 },
    { cardId: 'cre_light_herald_dusk', count: 2 },
    { cardId: 'cre_light_knight', count: 2 },
    { cardId: 'cre_light_avenger', count: 1 },
    { cardId: 'cre_light_paladin', count: 1 },
    { cardId: 'art_light_holy_light', count: 3 },
    { cardId: 'art_light_blessing', count: 3 },
    { cardId: 'art_light_shield', count: 1 },
    { cardId: 'art_light_smite', count: 1 },
    { cardId: 'mana_light_sunny', count: 1 },
    { cardId: 'mana_light_temple', count: 1 },
    { cardId: 'mana_light_sanctuary', count: 1 },
  ],
};

// ============================================================================
// FAE DECK - Alli
// ============================================================================

// COMMANDER
export const ALLI: CommanderCard = {
  id: 'cmd_fae_alli',
  name: 'Alli',
  type: CardType.COMMANDER,
  pronouns: 'She/Her',
  subtitle: 'Game Master - Changeling Trickster',
  health: 30,
  elementType: ElementType.FAE,
  flavorText: 'Did you seriously just mouth off to the Winter Queen?',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/CardFrontAlli.png',
  abilities: [
    {
      id: 'alli_discard',
      cost: {},
      description: 'Choose a random card from any other player\'s hand. Choose to discard it or look at it and then return it.',
      effect: (game: GameState, playerId: number) => {
        const opponentId = getOpponentId(playerId);
        const opponent = game.players[opponentId];

        if (opponent.hand.length === 0) {
          logEvent(game, 'ability_activated', 'Opponent has no cards in hand.');
          return;
        }

        // Pick a random card from opponent's hand
        const randomIndex = Math.floor(Math.random() * opponent.hand.length);
        const randomCardId = opponent.hand[randomIndex];
        const cardName = game.cardInstances[randomCardId]?.name || randomCardId;

        // Ask player if they want to discard it
        createBinaryChoice(
          game,
          playerId,
          'Mind Games',
          `Revealed: ${cardName}. Discard it or return it?`,
          () => {
            // Discard the card
            opponent.hand.splice(randomIndex, 1);
            opponent.discard.push(randomCardId);
            logEvent(game, 'ability_activated', `${cardName} was discarded.`);
          },
          () => {
            // Return it (do nothing)
            logEvent(game, 'ability_activated', `${cardName} was returned to hand.`);
          },
          'Discard',
          'Return',
          'cmd_fae_alli'
        );
      },
    },
    {
      id: 'alli_transform',
      cost: { [ElementType.FAE]: 1 },
      description: 'Target creature becomes a copy of another creature for one round.',
      effect: (game: GameState, playerId: number) => {
        // First, select the creature to transform
        requestTargets(
          game,
          {
            playerId,
            title: 'Fae Transformation - Select Creature to Transform',
            description: 'Choose a creature to transform',
            targetType: 'creature',
          },
          (selection1: TargetSelection) => {
            if (selection1.creatures && selection1.creatures.length > 0) {
              const transformTargetId = selection1.creatures[0];

              // Then, select what to transform it into
              requestTargets(
                game,
                {
                  playerId,
                  title: 'Fae Transformation - Select Copy Target',
                  description: 'Choose what to transform it into',
                  targetType: 'creature',
                  filter: (targetId: string) => targetId !== transformTargetId, // Can't copy itself
                },
                (selection2: TargetSelection) => {
                  if (selection2.creatures && selection2.creatures.length > 0) {
                    const copySourceId = selection2.creatures[0];
                    // Apply temporary transformation (stores original stats, restores at end of round)
                    applyTemporaryTransformation(
                      game,
                      transformTargetId,
                      copySourceId,
                      OngoingEffectDuration.END_OF_ROUND,
                      playerId
                    );
                    logEvent(game, 'ability_activated', 'Creature transformed until end of round!');
                  }
                }
              );
            }
          }
        );
      },
    },
    {
      id: 'alli_modal',
      cost: { [ElementType.FAE]: 2 },
      description: 'Choose one: Draw two cards, or return a creature to its owner\'s hand. If returned, its cost is reduced by two.',
      effect: (game: GameState, playerId: number) => {
        createChooseOneModal(
          game,
          playerId,
          'Fae Bargain',
          'Choose one:',
          [
            { id: 'draw', label: 'Draw 2 cards', description: 'Draw two cards from your deck' },
            { id: 'bounce', label: 'Bounce creature', description: 'Return target creature to hand (cost reduced by 2)' },
          ],
          (selectedId: string) => {
            if (selectedId === 'draw') {
              drawCards(game, playerId, 2);
            } else {
              // Request target selection for bounce
              requestTargets(
                game,
                {
                  playerId,
                  title: 'Fae Bargain',
                  description: 'Return target creature to its owner\'s hand',
                  targetType: 'creature',
                },
                (selection: TargetSelection) => {
                  if (selection.creatures && selection.creatures.length > 0) {
                    const targetCreature = findCreatureOnBattlefield(game, selection.creatures[0]);
                    if (targetCreature) {
                      const cardId = targetCreature.cardId;
                      const ownerId = targetCreature.ownerId;

                      // Return creature to hand
                      returnCreatureToHand(game, selection.creatures[0]);

                      // Add cost reduction for the bounced creature
                      addCardCostReduction(game, ownerId, cardId, 2);

                      logEvent(game, 'ability_activated', 'Creature returned to hand with cost reduced by 2!');
                    }
                  }
                }
              );
            }
          },
          'cmd_fae_alli'
        );
      },
    },
    {
      id: 'alli_swap',
      cost: { [ElementType.FAE]: 3 },
      description: 'Swap control of two target creatures until end of turn.',
      effect: (game: GameState, playerId: number) => {
        requestTargets(
          game,
          {
            playerId,
            title: 'Fae Trickery',
            description: 'Select two creatures to swap control until end of turn',
            targetType: 'creature',
            minTargets: 2,
            maxTargets: 2,
          },
          (selection: TargetSelection) => {
            if (selection.creatures && selection.creatures.length === 2) {
              const creature1Id = selection.creatures[0];
              const creature2Id = selection.creatures[1];

              const creature1 = findCreatureOnBattlefield(game, creature1Id);
              const creature2 = findCreatureOnBattlefield(game, creature2Id);

              if (creature1 && creature2) {
                // Swap control - each creature gets the other's controller
                swapCreatureControl(game, creature1Id, creature2.ownerId, OngoingEffectDuration.END_OF_TURN);
                swapCreatureControl(game, creature2Id, creature1.ownerId, OngoingEffectDuration.END_OF_TURN);

                logEvent(game, 'ability_activated', 'Creatures\' control swapped until end of turn!');
              }
            }
          }
        );
      },
    },
  ],
};

// CREATURES
export const PIXIE_TROUBLEMAKER: CreatureCard = {
  id: 'cre_fae_pixie',
  name: 'Pixie Troublemaker',
  type: CardType.CREATURE,
  cost: { [ElementType.FAE]: 1 },
  power: 1,
  health: 1,
  rarity: Rarity.COMMON,
  subtype: 'Fae, Pixie',
  keywords: [AbilityKeyword.FLYING],
  flavorText: 'Too small to hit, too annoying to ignore.',
};

export const MIRROR_SPRITE: CreatureCard = {
  id: 'cre_fae_mirror_sprite',
  name: 'Mirror Sprite',
  type: CardType.CREATURE,
  cost: { [ElementType.FAE]: 2 },
  power: 1,
  health: 2,
  rarity: Rarity.COMMON,
  subtype: 'Fae, Sprite',
  keywords: [],
  flavorText: 'Secrets are just stories waiting to be told.',
  abilities: [
    {
      id: 'mirror_sprite_enter',
      description: 'When this creature enters play, look at target player\'s hand.',
      trigger: 'enter',
      effect: (game: GameState, creatureId: string) => {
        const creature = game.players.flatMap((p) => p.battlefield.creatures).find((c) => c.instanceId === creatureId);
        if (!creature) return;

        const opponentId = getOpponentId(creature.ownerId);
        const opponent = game.players[opponentId];

        // Convert opponent's hand to revealed card format
        const revealedCards = opponent.hand.map(cardId => {
          const cardData = game.cardInstances[cardId];
          return {
            id: cardId,
            name: cardData?.name || cardId,
            imageUrl: cardData?.imageUrl,
          };
        });

        createRevealInfoModal(
          game,
          creature.ownerId,
          'Mirror Sprite',
          `Opponent's hand (${revealedCards.length} cards):`,
          revealedCards,
          () => {},
          'cre_fae_mirror_sprite'
        );
      },
    },
  ],
};

export const CHANGELING_MIMIC: CreatureCard = {
  id: 'cre_fae_changeling',
  name: 'Changeling Mimic',
  type: CardType.CREATURE,
  cost: { [ElementType.FAE]: 2 },
  power: 2,
  health: 2,
  rarity: Rarity.UNCOMMON,
  subtype: 'Fae, Changeling',
  keywords: [],
  abilities: [
    {
      id: 'changeling_mimic',
      description: 'When this card is played, choose any other card on the field. This card gets that card\'s stats and abilities.',
      trigger: 'enter',
      effect: (game: GameState, creatureId: string) => {
        const creature = findCreatureOnBattlefield(game, creatureId);
        if (!creature) return;

        requestTargets(game, {
          playerId: creature.ownerId,
          title: 'Changeling Mimic',
          description: 'Choose a creature to copy (stats and abilities)',
          targetType: 'creature',
          filter: (targetId: string, game: GameState) => {
            // Can target any creature except itself
            return targetId !== creatureId;
          },
          sourceCardId: 'cre_fae_changeling',
          sourceAbilityId: 'changeling_mimic',
        }, (selection: TargetSelection) => {
          if (selection.creatures && selection.creatures.length > 0) {
            copyCreatureStats(game, creatureId, selection.creatures[0]);
          }
        });
      },
    },
  ],
  flavorText: 'Identity is whatever you make it.',
};

export const TRICKSTER_SPIRIT: CreatureCard = {
  id: 'cre_fae_trickster',
  name: 'Trickster Spirit',
  type: CardType.CREATURE,
  cost: { [ElementType.FAE]: 3 },
  power: 2,
  health: 3,
  rarity: Rarity.UNCOMMON,
  subtype: 'Fae, Spirit',
  keywords: [],
  abilities: [
    {
      id: 'trickster_chaos',
      description: 'When this card is played, all players must discard a random card from their hand.',
      trigger: 'enter',
      effect: (game: GameState, creatureId: string) => {
        for (const player of game.players) {
          discardRandomCard(game, player.id);
        }
      },
    },
  ],
  flavorText: "What's yours is negotiable.",
};

export const FAE_SHAPESHIFTER: CreatureCard = {
  id: 'cre_fae_shapeshifter',
  name: 'Fae Shapeshifter',
  type: CardType.CREATURE,
  cost: { [ElementType.FAE]: 4 },
  power: 3,
  health: 3,
  rarity: Rarity.UNCOMMON,
  subtype: 'Fae, Shapeshifter',
  keywords: [],
  abilities: [
    {
      id: 'perfect_copy',
      description: 'TAP: Discard this card and add an exact copy of any other card on the field into play under your control.',
      trigger: 'activated',
      tapCost: true,
      effect: (game: GameState, creatureId: string) => {
        const creature = findCreatureOnBattlefield(game, creatureId);
        if (!creature) return;

        requestTargets(
          game,
          {
            playerId: creature.ownerId,
            title: 'Fae Shapeshifter',
            description: 'Transform into an exact copy of target creature',
            targetType: 'creature',
            filter: (targetId: string) => targetId !== creatureId, // Exclude itself
            sourceCardId: 'cre_fae_shapeshifter',
            sourceAbilityId: 'perfect_copy',
          },
          (selection: TargetSelection) => {
            if (selection.creatures && selection.creatures.length > 0) {
              transformCreatureIntoCopy(game, creatureId, selection.creatures[0]);
            }
          }
        );
      },
    },
  ],
  flavorText: 'Why be yourself when you can be anyone?',
};

// ARTS
export const MISDIRECTION: ArtCard = {
  id: 'art_fae_misdirection',
  name: 'Misdirection',
  type: CardType.ART,
  cost: { [ElementType.FAE]: 1 },
  rarity: Rarity.COMMON,
  flavorText: "Now you see it, now you don't.",
  effect: (game: GameState, playerId: number) => {
    requestTargets(
      game,
      {
        playerId,
        title: 'Misdirection',
        description: 'Return target creature with power 2 or less to its owner\'s hand',
        targetType: 'creature',
        filter: (targetId: string, game: GameState) => {
          const creature = findCreatureOnBattlefield(game, targetId);
          return creature !== null && creature.power <= 2;
        },
      },
      (selection: TargetSelection) => {
        if (selection.creatures && selection.creatures.length > 0) {
          returnCreatureToHand(game, selection.creatures[0]);
        }
      }
    );
  },
  targetType: 'creature',
  targetCount: 1,
};

export const FAE_BARGAIN: ArtCard = {
  id: 'art_fae_bargain',
  name: 'Fae Bargain',
  type: CardType.ART,
  cost: { [ElementType.FAE]: 2 },
  rarity: Rarity.COMMON,
  flavorText: 'A gift freely given... against your will.',
  effect: (game: GameState, playerId: number) => {
    // Each player discards random and draws (simplified)
    for (const player of game.players) {
      discardRandomCard(game, player.id);
      drawCards(game, player.id, 1);
    }
    // Note: Full implementation would need UI for card selection
  },
  targetType: 'none',
};

export const CHAOTIC_BLAST: ArtCard = {
  id: 'art_fae_chaotic_blast',
  name: 'Chaotic Blast',
  type: CardType.ART,
  cost: { [ElementType.FAE]: 2 },
  rarity: Rarity.UNCOMMON,
  flavorText: "Chaos doesn't play favorites... or does it?",
  effect: (game: GameState, playerId: number) => {
    const randomPlayerId = Math.floor(Math.random() * game.players.length);
    const targetId = `player_${randomPlayerId}`;
    const damage = rollDieTracked(game, 6, playerId, 'damage', targetId, 'player');
    dealDamage(game, targetId, 'player', damage);
  },
  targetType: 'none',
};

export const WILD_MAGIC_SURGE: ArtCard = {
  id: 'art_fae_wild_magic',
  name: 'Wild Magic Surge',
  type: CardType.ART,
  cost: { [ElementType.FAE]: 3 },
  rarity: Rarity.RARE,
  flavorText: 'Sometimes the best trick is starting over.',
  effect: (game: GameState, playerId: number) => {
    for (const player of game.players) {
      const handSize = player.hand.length;
      // Shuffle hand back into deck
      player.deck.push(...player.hand);
      player.hand = [];
      // Shuffle deck
      for (let i = player.deck.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [player.deck[i], player.deck[j]] = [player.deck[j], player.deck[i]];
      }
      // Draw the same number of cards
      drawCards(game, player.id, handSize);
    }
  },
  targetType: 'none',
};

// MANA SOURCES
export const FAIR_FOLK_GLEN: ManaSourceCard = {
  id: 'mana_fae_glen',
  name: 'Fair Folk Glen',
  type: CardType.MANA_SOURCE,
  rarity: Rarity.COMMON,
  elementType: ElementType.FAE,
  manaGenerated: { [ElementType.FAE]: 1 },
  flavorText: 'An enchanting space - mysterious and mystical.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/FairFolkGlen.png',
};

export const SHIFTING_SEASON: ManaSourceCard = {
  id: 'mana_fae_season',
  name: 'Shifting Season',
  type: CardType.MANA_SOURCE,
  cost: { [ElementType.FAE]: 1 },
  rarity: Rarity.UNCOMMON,
  elementType: ElementType.FAE,
  manaGenerated: { [ElementType.FAE]: 1 },
  flavorText: 'The future is negotiable.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/ShiftingSeason.png',
  abilities: [
    {
      id: 'season_scry',
      description: 'Look at top card of your deck. You may put it on the bottom of your deck.',
      effect: (game: GameState, playerId: number) => {
        const player = game.players[playerId];
        if (player.deck.length > 0) {
          const topCard = player.deck[0];
          const topCardName = game.cardInstances[topCard]?.name || topCard;

          createBinaryChoice(
            game,
            playerId,
            'Shifting Season',
            `Top card: ${topCardName}. Move to bottom of deck?`,
            () => {
              // Yes - move to bottom
              const card = player.deck.shift();
              if (card) {
                player.deck.push(card);
                logEvent(game, 'ability_activated', `Moved ${topCardName} to bottom of deck.`);
              }
            },
            () => {
              // No - leave on top
              logEvent(game, 'ability_activated', `Kept ${topCardName} on top of deck.`);
            },
            'Move to Bottom',
            'Keep on Top',
            'mana_fae_season'
          );
        }
      },
    },
  ],
};

export const ENCHANTED_CIRCLE: ManaSourceCard = {
  id: 'mana_fae_circle',
  name: 'Enchanted Circle',
  type: CardType.MANA_SOURCE,
  cost: { [ElementType.FAE]: 1 },
  rarity: Rarity.UNCOMMON,
  elementType: ElementType.FAE,
  manaGenerated: { [ElementType.FAE]: 1 },
  flavorText: 'Step inside at your own risk.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/EnchantedCircle.png',
  abilities: [
    {
      id: 'circle_bonus',
      description: 'Flip a coin. If heads, gain 1 additional Fae Mana.',
      effect: (game: GameState, playerId: number) => {
        const flipResult = Math.random() < 0.5;
        if (flipResult) {
          const player = game.players[playerId];
          addMana(player.manaPool, { [ElementType.FAE]: 1 });
        }
      },
    },
  ],
};

export const FAE_DECK: DeckDefinition = {
  name: 'Fae Starter Deck',
  elementType: ElementType.FAE,
  commanderId: 'cmd_fae_alli',
  cards: [
    { cardId: 'cre_fae_pixie', count: 3 },
    { cardId: 'cre_fae_mirror_sprite', count: 3 },
    { cardId: 'cre_fae_changeling', count: 2 },
    { cardId: 'cre_fae_trickster', count: 2 },
    { cardId: 'cre_fae_shapeshifter', count: 1 },
    { cardId: 'art_fae_misdirection', count: 3 },
    { cardId: 'art_fae_bargain', count: 3 },
    { cardId: 'art_fae_chaotic_blast', count: 2 },
    { cardId: 'art_fae_wild_magic', count: 1 },
    { cardId: 'mana_fae_glen', count: 1 },
    { cardId: 'mana_fae_season', count: 1 },
    { cardId: 'mana_fae_circle', count: 1 },
  ],
};

// ============================================================================
// WIND DECK - Doug
// ============================================================================

// COMMANDER
export const DOUG: CommanderCard = {
  id: 'cmd_wind_doug',
  name: 'Doug',
  type: CardType.COMMANDER,
  pronouns: 'He/Him',
  subtitle: 'Game Master - Human Wizard',
  health: 30,
  elementType: ElementType.WIND,
  flavorText: "Let's try it and see where we end up.",
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/CardFrontDoug.png',
  abilities: [
    {
      id: 'doug_mana',
      cost: {},
      tapCost: true,
      description: 'TAP: Gain 1 Wind Mana',
      effect: (game: GameState, playerId: number) => {
        addMana(game.players[playerId].manaPool, { [ElementType.WIND]: 1 });
      },
    },
    {
      id: 'doug_flying',
      cost: { [ElementType.WIND]: 1 },
      description: 'Target creature gains Flying until end of turn. (Can only be blocked by creatures with Flying)',
      effect: (game: GameState, playerId: number) => {
        // Request target selection for creature
        requestTargets(
          game,
          {
            playerId,
            title: 'Windborne',
            description: 'Target creature gains Flying until end of turn',
            targetType: 'creature',
          },
          (selection: TargetSelection) => {
            if (selection.creatures && selection.creatures.length > 0) {
              const creatureId = selection.creatures[0];
              // Add temporary Flying keyword via ongoing effect
              addOngoingEffect(game, {
                type: OngoingEffectType.KEYWORD_GRANT,
                duration: OngoingEffectDuration.END_OF_TURN,
                targetId: creatureId,
                targetType: 'creature',
                createdByPlayerId: playerId,
                keyword: AbilityKeyword.FLYING,
              });
              logEvent(game, 'ability_activated', 'Creature gains Flying until end of turn!');
            }
          }
        );
      },
    },
    {
      id: 'doug_bounce',
      cost: { [ElementType.WIND]: 2 },
      description: 'Return target creature you control to your hand. Draw a card.',
      effect: (game: GameState, playerId: number) => {
        // Return first creature to hand
        const player = game.players[playerId];
        if (player.battlefield.creatures.length > 0) {
          const creature = player.battlefield.creatures[0];
          returnCreatureToHand(game, creature.instanceId);
        }
        drawCards(game, playerId, 1);
      },
    },
    {
      id: 'doug_removal',
      cost: { [ElementType.WIND]: 3 },
      description: 'Put target creature on top of its owner\'s deck',
      effect: (game: GameState, playerId: number) => {
        // Get opponent's first creature and put on top of their deck
        const opponentId = getOpponentId(playerId);
        const opponent = game.players[opponentId];

        if (opponent.battlefield.creatures.length > 0) {
          const creature = opponent.battlefield.creatures[0];
          const cardId = creature.cardId;

          // Remove from battlefield
          opponent.battlefield.creatures.splice(0, 1);

          // Put on top of owner's deck (not discard, not token)
          if (!cardId.startsWith('token_')) {
            opponent.deck.unshift(cardId);
          }
        }
      },
    },
  ],
};

// CREATURES
export const SKY_SPRITE: CreatureCard = {
  id: 'cre_wind_sky_sprite',
  name: 'Sky Sprite',
  type: CardType.CREATURE,
  cost: { [ElementType.WIND]: 1 },
  power: 1,
  health: 1,
  rarity: Rarity.COMMON,
  subtype: 'Elemental, Sprite',
  keywords: [AbilityKeyword.FLYING],
  flavorText: 'Too quick to catch, too clever to corner.',
};

export const STORM_CROW: CreatureCard = {
  id: 'cre_wind_storm_crow',
  name: 'Storm Crow',
  type: CardType.CREATURE,
  cost: { [ElementType.WIND]: 2 },
  power: 2,
  health: 1,
  rarity: Rarity.COMMON,
  subtype: 'Elemental, Bird',
  keywords: [AbilityKeyword.FLYING],
  flavorText: 'The wind carries its call across miles.',
  abilities: [
    {
      id: 'storm_crow_bounce',
      description: 'When this creature deals damage to a GM, you may return it to your hand. If you do, draw a card.',
      trigger: 'damage',
      effect: (game: GameState, creatureId: string) => {
        // Check if this creature dealt damage to any GM this turn
        if (!didCreatureDamageAnyGM(game, creatureId)) return;

        const creature = findCreatureOnBattlefield(game, creatureId);
        if (!creature) return;

        // Ask player if they want to bounce
        createBinaryChoice(
          game,
          creature.ownerId,
          'Storm Crow',
          'Return Storm Crow to your hand and draw a card?',
          () => {
            // Yes - bounce and draw
            returnCreatureToHand(game, creatureId);
            drawCards(game, creature.ownerId, 1);
          },
          () => {
            // No - do nothing
          },
          'Return & Draw',
          'Keep on Battlefield',
          'cre_wind_storm_crow'
        );
      },
    },
  ],
};

export const ZEPHYR_DANCER: CreatureCard = {
  id: 'cre_wind_zephyr',
  name: 'Zephyr Dancer',
  type: CardType.CREATURE,
  cost: { [ElementType.WIND]: 2 },
  power: 1,
  health: 3,
  rarity: Rarity.UNCOMMON,
  subtype: 'Elemental',
  keywords: [AbilityKeyword.FLYING],
  flavorText: 'Lift your spirits and your feet.',
  abilities: [
    {
      id: 'zephyr_bounce',
      description: 'When this creature enters play, you may return target creature you control to its owner\'s hand.',
      trigger: 'enter',
      effect: (game: GameState, creatureId: string) => {
        const creature = findCreatureOnBattlefield(game, creatureId);
        if (!creature) return;

        requestTargets(game, {
          playerId: creature.ownerId,
          title: 'Zephyr Dancer',
          description: 'Return target creature you control to your hand (optional)',
          targetType: 'creature',
          minTargets: 0, // Optional - can choose 0
          maxTargets: 1,
          filter: (targetId: string, game: GameState) => {
            const target = findCreatureOnBattlefield(game, targetId);
            return target !== null && target.ownerId === creature.ownerId;
          },
          sourceCardId: 'cre_wind_zephyr',
          sourceAbilityId: 'zephyr_bounce',
        }, (selection: TargetSelection) => {
          if (selection.creatures && selection.creatures.length > 0) {
            returnCreatureToHand(game, selection.creatures[0]);
          }
        });
      },
    },
  ],
};

export const GALE_RIDER: CreatureCard = {
  id: 'cre_wind_gale_rider',
  name: 'Gale Rider',
  type: CardType.CREATURE,
  cost: { [ElementType.WIND]: 3 },
  power: 3,
  health: 2,
  rarity: Rarity.UNCOMMON,
  subtype: 'Human, Mounted',
  keywords: [AbilityKeyword.FLYING],
  flavorText: 'Strike from above, vanish like mist.',
  abilities: [
    {
      id: 'gale_bounce',
      description: 'When this creature deals damage, you may return it to your hand. If you do, draw a card.',
      trigger: 'damage',
      effect: (game: GameState, creatureId: string) => {
        // Check if this creature dealt damage to any GM this turn
        if (!didCreatureDamageAnyGM(game, creatureId)) return;

        const creature = findCreatureOnBattlefield(game, creatureId);
        if (!creature) return;

        // Ask player if they want to bounce
        createBinaryChoice(
          game,
          creature.ownerId,
          'Gale Rider',
          'Return Gale Rider to your hand and draw a card?',
          () => {
            // Yes - bounce and draw
            returnCreatureToHand(game, creatureId);
            drawCards(game, creature.ownerId, 1);
          },
          () => {
            // No - do nothing
          },
          'Return & Draw',
          'Keep on Battlefield',
          'cre_wind_gale_rider'
        );
      },
    },
  ],
};

export const TEMPEST_DRAKE: CreatureCard = {
  id: 'cre_wind_tempest_drake',
  name: 'Tempest Drake',
  type: CardType.CREATURE,
  cost: { [ElementType.WIND]: 4 },
  power: 5,
  health: 4,
  rarity: Rarity.RARE,
  subtype: 'Dragon',
  keywords: [AbilityKeyword.FLYING],
  flavorText: 'When the storm arrives, all must yield.',
  abilities: [
    {
      id: 'tempest_bounce',
      description: 'When this creature deals damage to a GM, return target creature controlled by that GM to its owner\'s hand.',
      trigger: 'damage',
      effect: (game: GameState, creatureId: string) => {
        // Check if this creature dealt damage to any GM this turn
        if (!didCreatureDamageAnyGM(game, creatureId)) return;

        const creature = findCreatureOnBattlefield(game, creatureId);
        if (!creature) return;

        // Find which GM was damaged to filter their creatures
        const damagedGMId = game.damageEventsThisTurn
          ?.filter(e => e.sourceId === creatureId && e.targetType === 'player')
          .map(e => parseInt(e.targetId.replace('player_', '')))[0];

        if (damagedGMId === undefined) return;

        requestTargets(game, {
          playerId: creature.ownerId,
          title: 'Tempest Drake',
          description: 'Return target creature to its owner\'s hand',
          targetType: 'creature',
          filter: (targetId: string, game: GameState) => {
            const target = findCreatureOnBattlefield(game, targetId);
            return target !== null && target.ownerId === damagedGMId;
          },
          sourceCardId: 'cre_wind_tempest_drake',
          sourceAbilityId: 'tempest_bounce',
        }, (selection: TargetSelection) => {
          if (selection.creatures && selection.creatures.length > 0) {
            returnCreatureToHand(game, selection.creatures[0]);
          }
        });
      },
    },
  ],
};

// ARTS
export const GUST: ArtCard = {
  id: 'art_wind_gust',
  name: 'Gust',
  type: CardType.ART,
  cost: { [ElementType.WIND]: 1 },
  rarity: Rarity.COMMON,
  flavorText: 'Rise above your limits.',
  effect: (game: GameState, playerId: number) => {
    requestTargets(
      game,
      {
        playerId,
        title: 'Gust',
        description: 'Target creature gains Flying',
        targetType: 'creature',
      },
      (selection: TargetSelection) => {
        if (selection.creatures && selection.creatures.length > 0) {
          const creature = findCreatureOnBattlefield(game, selection.creatures[0]);
          if (creature && !creature.keywords.includes(AbilityKeyword.FLYING)) {
            creature.keywords.push(AbilityKeyword.FLYING);
          }
        }
      }
    );
  },
  targetType: 'creature',
  targetCount: 1,
};

export const TAILWIND: ArtCard = {
  id: 'art_wind_tailwind',
  name: 'Tailwind',
  type: CardType.ART,
  cost: { [ElementType.WIND]: 2 },
  rarity: Rarity.COMMON,
  flavorText: 'The wind carries the bold.',
  effect: (game: GameState, playerId: number) => {
    const allCreatures = getAllCreatures(game);
    for (const creature of allCreatures) {
      modifyCreaturePower(game, creature.instanceId, 1, 0);
      if (!creature.keywords.includes(AbilityKeyword.FLYING)) {
        creature.keywords.push(AbilityKeyword.FLYING);
      }
    }
  },
  targetType: 'none',
};

export const UPDRAFT: ArtCard = {
  id: 'art_wind_updraft',
  name: 'Updraft',
  type: CardType.ART,
  cost: { [ElementType.WIND]: 2 },
  rarity: Rarity.UNCOMMON,
  flavorText: 'What goes up must come down... or sideways.',
  effect: (game: GameState, playerId: number) => {
    requestTargets(
      game,
      {
        playerId,
        title: 'Updraft',
        description: 'Return target creature to its owner\'s hand, then draw a card',
        targetType: 'creature',
      },
      (selection: TargetSelection) => {
        if (selection.creatures && selection.creatures.length > 0) {
          returnCreatureToHand(game, selection.creatures[0]);
        }
        drawCards(game, playerId, 1);
      }
    );
  },
  targetType: 'creature',
  targetCount: 1,
};

export const CYCLONE: ArtCard = {
  id: 'art_wind_cyclone',
  name: 'Cyclone',
  type: CardType.ART,
  cost: { [ElementType.WIND]: 4 },
  rarity: Rarity.RARE,
  flavorText: 'Let the storm sort them out.',
  effect: (game: GameState, playerId: number) => {
    const allCreatures = game.players.flatMap((p) => p.battlefield.creatures);
    const nonFlyingCreatures = allCreatures.filter(
      (c) => !c.keywords.includes(AbilityKeyword.FLYING)
    );
    for (const creature of nonFlyingCreatures) {
      returnCreatureToHand(game, creature.instanceId);
    }
  },
  targetType: 'none',
};

// MANA SOURCES
export const WINDSWEPT_PEAK: ManaSourceCard = {
  id: 'mana_wind_peak',
  name: 'Windswept Peak',
  type: CardType.MANA_SOURCE,
  rarity: Rarity.COMMON,
  elementType: ElementType.WIND,
  manaGenerated: { [ElementType.WIND]: 1 },
  flavorText: 'Where the air is thin and possibilities are endless.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/WindsweptPeak.png',
};

export const SKY_CURRENT: ManaSourceCard = {
  id: 'mana_wind_current',
  name: 'Sky Current',
  type: CardType.MANA_SOURCE,
  cost: { [ElementType.WIND]: 1 },
  rarity: Rarity.UNCOMMON,
  elementType: ElementType.WIND,
  manaGenerated: { [ElementType.WIND]: 1 },
  flavorText: 'Ride the winds of fortune.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/SkyCurrent.png',
  abilities: [
    {
      id: 'current_flying_draw',
      description: 'If you control a creature with Flying, draw a card.',
      effect: (game: GameState, playerId: number) => {
        const player = game.players[playerId];
        const hasFlying = player.battlefield.creatures.some((c) => {
          const creatureCard = game.cardInstances[c.cardId];
          return creatureCard?.type === CardType.CREATURE && creatureCard.keywords?.includes(AbilityKeyword.FLYING);
        });
        if (hasFlying) {
          drawCards(game, playerId, 1);
        }
      },
    },
  ],
};

export const STORM_SPIRE: ManaSourceCard = {
  id: 'mana_wind_spire',
  name: 'Storm Spire',
  type: CardType.MANA_SOURCE,
  cost: { [ElementType.WIND]: 1 },
  rarity: Rarity.UNCOMMON,
  elementType: ElementType.WIND,
  manaGenerated: { [ElementType.WIND]: 1 },
  flavorText: 'Where lightning strikes and eagles dare.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/StormSpire.png',
  abilities: [
    {
      id: 'spire_flying_buff',
      description: 'Target creature with Flying gets +1/+0 until end of turn.',
      effect: (game: GameState, playerId: number) => {
        const player = game.players[playerId];
        const flyingCreatures = player.battlefield.creatures.filter((c) => {
          const creatureCard = game.cardInstances[c.cardId];
          return creatureCard?.type === CardType.CREATURE && creatureCard.keywords?.includes(AbilityKeyword.FLYING);
        });
        if (flyingCreatures.length > 0) {
          requestTargets(
            game,
            {
              playerId,
              title: 'Storm Spire',
              description: 'Choose a creature with Flying to give +1/+0 until end of turn',
              targetType: 'creature',
              filter: (creatureId, g) => {
                const creature = findCreatureOnBattlefield(g, creatureId);
                if (!creature || creature.ownerId !== playerId) return false;
                const card = g.cardInstances[creature.cardId];
                return card?.type === CardType.CREATURE && card.keywords?.includes(AbilityKeyword.FLYING);
              },
            },
            (selection: TargetSelection) => {
              if (selection.creatures && selection.creatures.length > 0) {
                modifyCreaturePower(game, selection.creatures[0], 1, 0);
                logEvent(game, 'ability_activated', 'Storm Spire grants +1/+0!');
              }
            },
            () => {
              // Cancel - do nothing
            }
          );
        }
      },
    },
  ],
};

export const THUNDERHEAD_SUMMIT: ManaSourceCard = {
  id: 'mana_wind_summit',
  name: 'Thunderhead Summit',
  type: CardType.MANA_SOURCE,
  cost: { [ElementType.WIND]: 1 },
  rarity: Rarity.RARE,
  elementType: ElementType.WIND,
  manaGenerated: { [ElementType.WIND]: 2 },
  flavorText: 'At the peak, all paths lead down.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/ThunderheadSummit.png',
  abilities: [
    {
      id: 'summit_bounce',
      description: 'Return target creature you control to your hand.',
      effect: (game: GameState, playerId: number) => {
        const player = game.players[playerId];
        if (player.battlefield.creatures.length > 0) {
          requestTargets(
            game,
            {
              playerId,
              title: 'Thunderhead Summit',
              description: 'Choose a creature you control to return to your hand',
              targetType: 'creature',
              filter: (creatureId, g) => {
                const creature = findCreatureOnBattlefield(g, creatureId);
                return creature ? creature.ownerId === playerId : false;
              },
            },
            (selection: TargetSelection) => {
              if (selection.creatures && selection.creatures.length > 0) {
                returnCreatureToHand(game, selection.creatures[0]);
                logEvent(game, 'ability_activated', 'Thunderhead Summit returns creature to hand!');
              }
            },
            () => {
              // Cancel - do nothing
            }
          );
        }
      },
    },
  ],
};

export const WIND_DECK: DeckDefinition = {
  name: 'Wind Starter Deck',
  elementType: ElementType.WIND,
  commanderId: 'cmd_wind_doug',
  cards: [
    { cardId: 'cre_wind_sky_sprite', count: 3 },
    { cardId: 'cre_wind_storm_crow', count: 3 },
    { cardId: 'cre_wind_zephyr', count: 2 },
    { cardId: 'cre_wind_gale_rider', count: 2 },
    { cardId: 'cre_wind_tempest_drake', count: 1 },
    { cardId: 'art_wind_gust', count: 3 },
    { cardId: 'art_wind_tailwind', count: 3 },
    { cardId: 'art_wind_updraft', count: 2 },
    { cardId: 'art_wind_cyclone', count: 1 },
    { cardId: 'mana_wind_peak', count: 2 },
    { cardId: 'mana_wind_current', count: 1 },
    { cardId: 'mana_wind_spire', count: 1 },
    { cardId: 'mana_wind_summit', count: 1 },
  ],
};

// ============================================================================
// TOKEN CREATURES
// ============================================================================

export const TOKEN_MUSHROOM: CreatureCard = {
  id: 'token_Mushroom',
  name: 'Mushroom',
  type: CardType.CREATURE,
  cost: {},
  power: 1,
  health: 1,
  rarity: Rarity.COMMON,
  elementType: ElementType.EARTH,
  keywords: [],
  flavorText: 'A mystical fungus sprouted from the forest floor.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/MushroomToken.png',
};

export const TOKEN_SPORE: CreatureCard = {
  id: 'token_Spore',
  name: 'Spore',
  type: CardType.CREATURE,
  cost: {},
  power: 1,
  health: 1,
  rarity: Rarity.COMMON,
  elementType: ElementType.EARTH,
  keywords: [],
  flavorText: 'A tiny seed of life, drifting on the wind.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/SporeToken.png',
};

export const TOKEN_SAPLING: CreatureCard = {
  id: 'token_Sapling',
  name: 'Sapling',
  type: CardType.CREATURE,
  cost: {},
  power: 2,
  health: 2,
  rarity: Rarity.COMMON,
  elementType: ElementType.EARTH,
  keywords: [],
  flavorText: 'Young life taking root in the forest.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/SaplingToken.png',
};

export const TOKEN_SAPLING_1_1: CreatureCard = {
  id: 'token_Sapling_1_1',
  name: 'Sapling',
  type: CardType.CREATURE,
  cost: {},
  power: 1,
  health: 1,
  rarity: Rarity.COMMON,
  elementType: ElementType.EARTH,
  keywords: [],
  flavorText: 'A tiny sprout, fragile but full of potential.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/SaplingToken1-1.png',
};

export const TOKEN_BEAST: CreatureCard = {
  id: 'token_Beast',
  name: 'Beast',
  type: CardType.CREATURE,
  cost: {},
  power: 2,
  health: 2,
  rarity: Rarity.COMMON,
  elementType: ElementType.EARTH,
  keywords: [],
  flavorText: 'A primal creature of the wild.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/BeastToken.png',
};

export const TOKEN_VINE_PANTHER: CreatureCard = {
  id: 'token_Vine_Panther',
  name: 'Vine Panther',
  type: CardType.CREATURE,
  cost: {},
  power: 2,
  health: 2,
  rarity: Rarity.COMMON,
  elementType: ElementType.EARTH,
  keywords: [AbilityKeyword.BLOCK],
  flavorText: 'A guardian of the forest, wrapped in nature\'s embrace.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/VinePantherToken.png',
};

export const TOKEN_ICE_ELEMENTAL: CreatureCard = {
  id: 'token_Ice_Elemental',
  name: 'Ice Elemental',
  type: CardType.CREATURE,
  cost: {},
  power: 3,
  health: 3,
  rarity: Rarity.COMMON,
  elementType: ElementType.WATER,
  keywords: [],
  flavorText: 'A frozen manifestation of winter\'s fury.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/IceElementalToken.png',
};

export const TOKEN_PHANTOM_SAILOR: CreatureCard = {
  id: 'token_Phantom_Sailor',
  name: 'Phantom Sailor',
  type: CardType.CREATURE,
  cost: {},
  power: 2,
  health: 2,
  rarity: Rarity.COMMON,
  elementType: ElementType.WATER,
  keywords: [],
  flavorText: 'A ghostly mariner, forever sailing the ethereal seas.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/PhantomSailorToken.png',
};

export const TOKEN_DEMON_CAT: CreatureCard = {
  id: 'token_Demon Cat',
  name: 'Demon Cat',
  type: CardType.CREATURE,
  cost: {},
  power: 1,
  health: 1,
  rarity: Rarity.COMMON,
  elementType: ElementType.DARK,
  keywords: [],
  flavorText: 'A mischievous feline from the shadows.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/DemonCatToken.png',
};

export const TOKEN_SKELETON: CreatureCard = {
  id: 'token_Skeleton',
  name: 'Skeleton',
  type: CardType.CREATURE,
  cost: {},
  power: 1,
  health: 1,
  rarity: Rarity.COMMON,
  elementType: ElementType.DARK,
  keywords: [],
  flavorText: 'Bones animated by dark magic.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/SkeletonToken.png',
};

export const TOKEN_ZOMBIE: CreatureCard = {
  id: 'token_Zombie',
  name: 'Zombie',
  type: CardType.CREATURE,
  cost: {},
  power: 2,
  health: 2,
  rarity: Rarity.COMMON,
  elementType: ElementType.DARK,
  keywords: [],
  flavorText: 'The undead rise from their graves.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/ZombieToken.png',
};

export const TOKEN_THIEF: CreatureCard = {
  id: 'token_Thief',
  name: 'Thief',
  type: CardType.CREATURE,
  cost: {},
  power: 1,
  health: 1,
  rarity: Rarity.COMMON,
  elementType: ElementType.DARK,
  keywords: [],
  flavorText: 'A shadowy figure with nimble fingers.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/ThiefToken.png',
};

export const TOKEN_OBSIDIAN_WALL: CreatureCard = {
  id: 'token_Obsidian_Wall',
  name: 'Obsidian Wall',
  type: CardType.CREATURE,
  cost: {},
  power: 0,
  health: 3,
  rarity: Rarity.COMMON,
  elementType: ElementType.FIRE,
  keywords: [AbilityKeyword.BLOCK],
  flavorText: 'An impenetrable barrier of volcanic glass.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/ObsidianWallToken.png',
};

export const TOKEN_MAGMA_ELEMENTAL: CreatureCard = {
  id: 'token_Magma_Elemental',
  name: 'Magma Elemental',
  type: CardType.CREATURE,
  cost: {},
  power: 3,
  health: 1,
  rarity: Rarity.COMMON,
  elementType: ElementType.FIRE,
  keywords: [],
  flavorText: 'A raging inferno given form. Must attack next turn.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/MagmaElementalToken.png',
};

export const TOKEN_STARDUST_SPRITE: CreatureCard = {
  id: 'token_Stardust_Sprite',
  name: 'Stardust Sprite',
  type: CardType.CREATURE,
  cost: {},
  power: 1,
  health: 1,
  rarity: Rarity.COMMON,
  elementType: ElementType.LIGHT,
  keywords: [AbilityKeyword.FLYING],
  flavorText: 'A celestial spark dancing through the air.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/StardustSpriteToken.png',
};

export const TOKEN_SEABIRD: CreatureCard = {
  id: 'token_Seabird',
  name: 'Seabird',
  type: CardType.CREATURE,
  cost: {},
  power: 1,
  health: 1,
  rarity: Rarity.COMMON,
  elementType: ElementType.WIND,
  keywords: [AbilityKeyword.FLYING],
  flavorText: 'A graceful bird soaring on ocean winds.',
  imageUrl: 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/SeabirdToken.png',
};

// ============================================================================
// CARD REGISTRY
// ============================================================================

export const ALL_CARDS: Record<string, Card> = {
  // Fire
  [DICE_AND_AUTISM.id]: DICE_AND_AUTISM,
  [EMBER_IMP.id]: EMBER_IMP,
  [FLAME_DANCER.id]: FLAME_DANCER,
  [RECKLESS_BERSERKER.id]: RECKLESS_BERSERKER,
  [MAGMA_HOUND.id]: MAGMA_HOUND,
  [WILDFIRE_PHOENIX.id]: WILDFIRE_PHOENIX,
  [SPARK.id]: SPARK,
  [BURNING_AMBITION.id]: BURNING_AMBITION,
  [CHAOS_BOLT.id]: CHAOS_BOLT,
  [PYROCLASM.id]: PYROCLASM,
  [VOLCANIC_ERUPTION.id]: VOLCANIC_ERUPTION,
  [VOLCANIC_CRAG.id]: VOLCANIC_CRAG,
  [RAGING_CALDERA.id]: RAGING_CALDERA,

  // Water
  [NIC.id]: NIC,
  [TIDE_SPRITE.id]: TIDE_SPRITE,
  [SILVERSCALE_SCOUT.id]: SILVERSCALE_SCOUT,
  [RIVER_HEALER.id]: RIVER_HEALER,
  [CORAL_GUARDIAN.id]: CORAL_GUARDIAN,
  [MOONLIT_SERPENT.id]: MOONLIT_SERPENT,
  [LEVIATHAN_OF_THE_DEEP.id]: LEVIATHAN_OF_THE_DEEP,
  [REFRESHING_RAIN.id]: REFRESHING_RAIN,
  [FLOWING_GRACE.id]: FLOWING_GRACE,
  [TIDAL_WISDOM.id]: TIDAL_WISDOM,
  [CRASHING_WAVE.id]: CRASHING_WAVE,
  [TSUNAMI.id]: TSUNAMI,
  [CRYSTALLINE_LAKE.id]: CRYSTALLINE_LAKE,
  [MYSTIC_SPRING.id]: MYSTIC_SPRING,
  [TIDAL_POOL.id]: TIDAL_POOL,

  // Earth
  [CARMA.id]: CARMA,
  [ROOTKIN_DEFENDER.id]: ROOTKIN_DEFENDER,
  [GROVE_GUARDIAN.id]: GROVE_GUARDIAN,
  [THORNBACK_BEAR.id]: THORNBACK_BEAR,
  [MIRE_DRUID.id]: MIRE_DRUID,
  [ANCIENT_TREANT.id]: ANCIENT_TREANT,
  [NOURISH.id]: NOURISH,
  [OVERGROWTH.id]: OVERGROWTH,
  [ENTANGLE.id]: ENTANGLE,
  [VERDANT_RENEWAL.id]: VERDANT_RENEWAL,
  [NATURES_WRATH.id]: NATURES_WRATH,
  [VERDANT_GROVE.id]: VERDANT_GROVE,
  [HEALING_SPRING.id]: HEALING_SPRING,
  [FERTILE_SOIL.id]: FERTILE_SOIL,
  [WORLDROOT_GROVE.id]: WORLDROOT_GROVE,

  // Dark
  [NEAL.id]: NEAL,
  [SHADOW_IMP.id]: SHADOW_IMP,
  [DUSK_STALKER.id]: DUSK_STALKER,
  [GRAVE_TENDER.id]: GRAVE_TENDER,
  [VOID_WRAITH.id]: VOID_WRAITH,
  [LUCIFER_SHADOW_PRINCE.id]: LUCIFER_SHADOW_PRINCE,
  [LILITH_NIGHTS_WHISPER.id]: LILITH_NIGHTS_WHISPER,
  [BAPHOMET_DREAMING_SHADOW.id]: BAPHOMET_DREAMING_SHADOW,
  [DARK_PACT.id]: DARK_PACT,
  [LIFE_DRAIN.id]: LIFE_DRAIN,
  [SHADOW_BOLT.id]: SHADOW_BOLT,
  [NECROMANTIC_RITUAL.id]: NECROMANTIC_RITUAL,
  [SHADOWY_CAVE.id]: SHADOWY_CAVE,
  [BONEYARD_SHRINE.id]: BONEYARD_SHRINE,

  // Light
  [BRANDY.id]: BRANDY,
  [DAWN_SCOUT.id]: DAWN_SCOUT,
  [ASPIRING_SQUIRE.id]: ASPIRING_SQUIRE,
  [HERALD_OF_DAWN.id]: HERALD_OF_DAWN,
  [HERALD_OF_DUSK.id]: HERALD_OF_DUSK,
  [CRUSADING_KNIGHT.id]: CRUSADING_KNIGHT,
  [CELESTIAL_AVENGER.id]: CELESTIAL_AVENGER,
  [LIGHTBRINGER_PALADIN.id]: LIGHTBRINGER_PALADIN,
  [HOLY_LIGHT.id]: HOLY_LIGHT,
  [DIVINE_BLESSING.id]: DIVINE_BLESSING,
  [RIGHTEOUS_SHIELD.id]: RIGHTEOUS_SHIELD,
  [SMITE.id]: SMITE,
  [SUNNY_BEAMS.id]: SUNNY_BEAMS,
  [SACRED_TEMPLE.id]: SACRED_TEMPLE,
  [RADIANT_SANCTUARY.id]: RADIANT_SANCTUARY,

  // Fae
  [ALLI.id]: ALLI,
  [PIXIE_TROUBLEMAKER.id]: PIXIE_TROUBLEMAKER,
  [MIRROR_SPRITE.id]: MIRROR_SPRITE,
  [CHANGELING_MIMIC.id]: CHANGELING_MIMIC,
  [TRICKSTER_SPIRIT.id]: TRICKSTER_SPIRIT,
  [FAE_SHAPESHIFTER.id]: FAE_SHAPESHIFTER,
  [MISDIRECTION.id]: MISDIRECTION,
  [FAE_BARGAIN.id]: FAE_BARGAIN,
  [CHAOTIC_BLAST.id]: CHAOTIC_BLAST,
  [WILD_MAGIC_SURGE.id]: WILD_MAGIC_SURGE,
  [FAIR_FOLK_GLEN.id]: FAIR_FOLK_GLEN,
  [SHIFTING_SEASON.id]: SHIFTING_SEASON,
  [ENCHANTED_CIRCLE.id]: ENCHANTED_CIRCLE,

  // Wind
  [DOUG.id]: DOUG,
  [SKY_SPRITE.id]: SKY_SPRITE,
  [STORM_CROW.id]: STORM_CROW,
  [ZEPHYR_DANCER.id]: ZEPHYR_DANCER,
  [GALE_RIDER.id]: GALE_RIDER,
  [TEMPEST_DRAKE.id]: TEMPEST_DRAKE,
  [GUST.id]: GUST,
  [TAILWIND.id]: TAILWIND,
  [UPDRAFT.id]: UPDRAFT,
  [CYCLONE.id]: CYCLONE,
  [WINDSWEPT_PEAK.id]: WINDSWEPT_PEAK,
  [SKY_CURRENT.id]: SKY_CURRENT,
  [STORM_SPIRE.id]: STORM_SPIRE,
  [THUNDERHEAD_SUMMIT.id]: THUNDERHEAD_SUMMIT,

  // Tokens
  [TOKEN_MUSHROOM.id]: TOKEN_MUSHROOM,
  [TOKEN_SPORE.id]: TOKEN_SPORE,
  [TOKEN_SAPLING.id]: TOKEN_SAPLING,
  [TOKEN_SAPLING_1_1.id]: TOKEN_SAPLING_1_1,
  [TOKEN_BEAST.id]: TOKEN_BEAST,
  [TOKEN_VINE_PANTHER.id]: TOKEN_VINE_PANTHER,
  [TOKEN_ICE_ELEMENTAL.id]: TOKEN_ICE_ELEMENTAL,
  [TOKEN_PHANTOM_SAILOR.id]: TOKEN_PHANTOM_SAILOR,
  [TOKEN_DEMON_CAT.id]: TOKEN_DEMON_CAT,
  [TOKEN_SKELETON.id]: TOKEN_SKELETON,
  [TOKEN_ZOMBIE.id]: TOKEN_ZOMBIE,
  [TOKEN_THIEF.id]: TOKEN_THIEF,
  [TOKEN_OBSIDIAN_WALL.id]: TOKEN_OBSIDIAN_WALL,
  [TOKEN_MAGMA_ELEMENTAL.id]: TOKEN_MAGMA_ELEMENTAL,
  [TOKEN_STARDUST_SPRITE.id]: TOKEN_STARDUST_SPRITE,
  [TOKEN_SEABIRD.id]: TOKEN_SEABIRD,
};

export const STARTER_DECKS: Record<string, DeckDefinition> = {
  fire: FIRE_DECK,
  water: WATER_DECK,
  earth: EARTH_DECK,
  dark: DARK_DECK,
  light: LIGHT_DECK,
  fae: FAE_DECK,
  wind: WIND_DECK,
};
